/**
 * <bundle-builder> Web Component
 *
 * Single-step bundle builder: Pick The Ends + Charms -> Add to Cart
 * Integrates with <charm-quiz> via applyQuizResults() for guided filtering.
 * Fires GA4 dataLayer events at each step for full tracking coverage.
 */

class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    this.state = {
      step: 1,
      bases: [],          // [{ variantId, productId, title, variantTitle, price, image, metal, quantity }]
      charms: [],         // [{ variantId, productId, title, variantTitle, price, image, cardEl }]
      filter: 'all',
      loading: false,
      quizFilters: null   // { vibes: [], metal: string } or null
    };

    this.tiers = JSON.parse(this.getAttribute('data-tiers') || '[]');
    this.cartType = this.getAttribute('data-cart-type') || 'drawer';
    this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
    this.discountPrefix = this.getAttribute('data-discount-prefix') || 'BUNDLE';
  }

  connectedCallback() {
    this.bindEvents();
    this.bindFooterEvents();
    this._deferredHydrated = false;
    this._productCache = new Map(); // handle → product JSON, for variant image fallback
    this._viewedProductIds = new Set(); // track view_item events fired (once per product per session)

    // Initialize step 1 footer button state
    this.goToStep(1);
    this.updateProgressTracker();
    this.updateSummaryBar();

    // Restore quiz state from sessionStorage if user refreshed
    this.restoreQuizFromSession();
  }

  /** Bind click events on the footer (which lives outside <bundle-builder>) */
  bindFooterEvents() {
    const root = this.getRootEl();
    const footer = root.querySelector('[data-progress-tracker]');
    if (!footer) return;

    footer.addEventListener('click', (e) => {
      const atcBtn = e.target.closest('[data-action="add-to-cart"]');
      if (atcBtn && !atcBtn.disabled) {
        this.addToCart();
        return;
      }
    });
  }

  restoreQuizFromSession() {
    try {
      const cached = sessionStorage.getItem('theends_quiz');
      if (!cached) return;

      const parsed = JSON.parse(cached);
      if (!parsed?.results?.recommendations) {
        sessionStorage.removeItem('theends_quiz');
        return;
      }

      // Store for later — modal buttons will decide what to do
      this._cachedQuizRestore = parsed;

      // Show the restore modal instead of auto-restoring
      const modal = document.querySelector('[data-restore-modal]');
      if (modal) {
        modal.hidden = false;
      } else {
        // No modal in DOM — clear session and let user start fresh
        sessionStorage.removeItem('theends_quiz');
        this._cachedQuizRestore = null;
      }
    } catch (e) {
      sessionStorage.removeItem('theends_quiz');
    }
  }

  /* ---------------------------------------------------------------
   * Event Binding
   * --------------------------------------------------------------- */
  bindEvents() {
    // Base card quantity controls
    this.querySelectorAll('.bundle-base-card').forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't toggle on stepper button clicks
        if (e.target.closest('[data-action="increase-base"]') || e.target.closest('[data-action="decrease-base"]')) return;
        this.toggleBase(card);
      });
    });

    // Filter tabs
    this.querySelectorAll('.bundle-filter-btn').forEach(btn => {
      btn.addEventListener('click', () => this.setFilter(btn.dataset.filter));
    });

    // Delegated click events
    this.addEventListener('click', (e) => {
      // Base card quantity: increase
      const increaseBase = e.target.closest('[data-action="increase-base"]');
      if (increaseBase) {
        const card = increaseBase.closest('.bundle-base-card');
        if (card) this.increaseBase(card);
        return;
      }

      // Base card quantity: decrease
      const decreaseBase = e.target.closest('[data-action="decrease-base"]');
      if (decreaseBase) {
        const card = decreaseBase.closest('.bundle-base-card');
        if (card) this.decreaseBase(card);
        return;
      }

      // Card quantity: increase charm (+ button or stepper +)
      const increaseBtn = e.target.closest('[data-action="increase-charm"]');
      if (increaseBtn) {
        const card = increaseBtn.closest('.bundle-charm-card');
        if (card && !card.dataset.groupCard) {
          // If this is a base product in the charms grid, route to base logic
          if (card.dataset.isBase) {
            this.addBaseFromGrid(card);
          } else {
            this.increaseCharmOnCard(card);
          }
        }
        return;
      }

      // Card quantity: decrease charm (stepper -)
      const decreaseBtn = e.target.closest('[data-action="decrease-charm"]');
      if (decreaseBtn) {
        const card = decreaseBtn.closest('.bundle-charm-card');
        if (card && !card.dataset.groupCard) {
          if (card.dataset.isBase) {
            this.removeBaseFromGrid(card);
          } else {
            this.decreaseCharmOnCard(card);
          }
        }
        return;
      }

      const variantBtn = e.target.closest('.bundle-charm-variant');
      if (variantBtn) {
        this.selectCharmVariant(variantBtn);
        return;
      }

      const charmCard = e.target.closest('.bundle-charm-card');
      if (charmCard && !e.target.closest('button') && !charmCard.dataset.groupCard) {
        // Click on card image/area opens detail modal; + button still quick-adds
        this.openCharmModal(charmCard);
        return;
      }

      const openCharmModalBtn = e.target.closest('[data-action="open-charm-modal"]');
      if (openCharmModalBtn) {
        const card = openCharmModalBtn.closest('.bundle-charm-card');
        if (card) this.openCharmModal(card);
        return;
      }

      const atcBtn = e.target.closest('[data-action="add-to-cart"]');
      if (atcBtn) {
        this.addToCart();
        return;
      }

      // Letter/Number group card → open modal
      const groupCard = e.target.closest('.bundle-charm-card--group');
      const openModalBtn = e.target.closest('[data-action="open-letter-modal"]');
      if (openModalBtn || (groupCard && !e.target.closest('[data-action]'))) {
        const card = groupCard || openModalBtn.closest('.bundle-charm-card--group');
        if (card) this.openLetterModal(card);
        return;
      }

      // Close letter modal
      const closeModal = e.target.closest('[data-action="close-letter-modal"]');
      if (closeModal) {
        this.closeLetterModal();
        return;
      }

      // Confirm letter modal
      const confirmModal = e.target.closest('[data-action="confirm-letter-modal"]');
      if (confirmModal) {
        this.confirmLetterModal();
        return;
      }

      // Charm detail modal actions
      const closeCharmModal = e.target.closest('[data-action="close-charm-modal"]');
      if (closeCharmModal) { this.closeCharmModal(); return; }

      const confirmCharmModal = e.target.closest('[data-action="confirm-charm-modal"]');
      if (confirmCharmModal) { this.confirmCharmModal(); return; }

      const charmModalQty = e.target.closest('[data-action="charm-modal-qty"]');
      if (charmModalQty) {
        this.updateCharmModalQty(parseInt(charmModalQty.dataset.delta, 10));
        return;
      }

      const charmModalSwatch = e.target.closest('.bundle-charm-modal__swatch');
      if (charmModalSwatch) { this.selectCharmModalVariant(charmModalSwatch); return; }

      const prevVariantBtn = e.target.closest('[data-action="charm-modal-prev-variant"]');
      if (prevVariantBtn) { this.cycleCharmModalVariant(-1); return; }

      const nextVariantBtn = e.target.closest('[data-action="charm-modal-next-variant"]');
      if (nextVariantBtn) { this.cycleCharmModalVariant(1); return; }

      // Clear quiz filters
      const clearBtn = e.target.closest('[data-action="clear-quiz-filters"]');
      if (clearBtn) {
        this.clearQuizFilters();
        return;
      }

      // Letter sub-filter
      const letterBtn = e.target.closest('[data-letter-filter-val]');
      if (letterBtn) {
        this.handleSubFilter(letterBtn.dataset.letterFilterVal, 'letter');
        return;
      }

      // Number sub-filter
      const numberBtn = e.target.closest('[data-number-filter-val]');
      if (numberBtn) {
        this.handleSubFilter(numberBtn.dataset.numberFilterVal, 'number');
        return;
      }
    });

    // Restore modal: Continue building
    const continueBtn = document.querySelector('[data-action="restore-continue"]');
    if (continueBtn) {
      continueBtn.addEventListener('click', () => {
        const modal = document.querySelector('[data-restore-modal]');
        if (modal) modal.hidden = true;

        if (this._cachedQuizRestore) {
          const { results } = this._cachedQuizRestore;
          this._cachedQuizRestore = null;

          // Skip path selector, go straight to builder
          const wrap = this.closest('.build-your-set-wrap');
          if (wrap) wrap.dataset.pageState = 'building';

          // Run quiz flow (will go to step 1 with pre-highlight per Fix 1)
          this.applyQuizResults(results);
        }
      });
    }

    // Restore modal: Start fresh
    const freshBtn = document.querySelector('[data-action="restore-fresh"]');
    if (freshBtn) {
      freshBtn.addEventListener('click', () => {
        const modal = document.querySelector('[data-restore-modal]');
        if (modal) modal.hidden = true;

        this._cachedQuizRestore = null;
        try { sessionStorage.removeItem('theends_quiz'); } catch (e) { /* ignore */ }
        // Page stays on path selector (default state)
      });
    }

    // Restore modal: Backdrop click = start fresh
    const backdrop = document.querySelector('.bundle-restore-modal__backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => {
        if (freshBtn) freshBtn.click();
      });
    }
  }

  /* ---------------------------------------------------------------
   * Quiz Integration
   * --------------------------------------------------------------- */

  /**
   * Called by the orchestrator when the charm quiz completes.
   * Auto-selects the base from the quiz metal choice and goes straight to step 2.
   */
  async applyQuizResults(results) {
    const { metal } = results;

    this.state.quizFilters = { ...results };

    // Show loader, hide panel while loading
    const loader = this.querySelector('[data-builder-loader]');
    if (loader) loader.hidden = false;
    this.querySelectorAll('.bundle-builder__panel').forEach(p => p.classList.remove('is-active'));

    await this.continueQuizFlow(results, metal);
  }

  /** Continues the quiz→picks→grid flow after base is determined */
  async continueQuizFlow(results, metal) {
    const { vibes, zodiac, recommendations } = results;

    // Render the "Your Picks" strip from Shopify product API
    await this.renderQuizPicks(recommendations, metal);

    // Hydrate deferred cards before filtering (quiz needs all cards in DOM)
    this.hydrateDeferredCharms();

    // Filter the browse grid to show vibe-matching charms
    this.applyQuizFiltersToGrid(recommendations, metal);

    // Show curated banner
    this.showCuratedBanner(vibes, zodiac, metal);

    // Hide normal filter tabs (quiz replaces them)
    const filtersWrap = this.querySelector('[data-filters-wrap]');
    if (filtersWrap) filtersWrap.hidden = true;


    // Auto-add The Ends base from the grid cards based on quiz metal choice
    this.autoAddBaseFromQuiz(metal);

    // Hide loader, show builder panel
    const loader = this.querySelector('[data-builder-loader]');
    if (loader) loader.hidden = true;
    this.goToStep(1);

    // Scroll to top
    setTimeout(() => this.scrollPageToBuilderTop(), 120);

    // Persist quiz results so page refresh doesn't lose state
    try {
      sessionStorage.setItem('theends_quiz', JSON.stringify({ results, metal }));
    } catch (e) { /* private browsing or quota exceeded — ignore */ }
  }

  /**
   * Auto-add The Ends base product based on quiz metal selection.
   * Gold → gold base, Silver → silver base.
   * "both" → pick the metal that matches the majority of selected charms.
   */
  autoAddBaseFromQuiz(metal) {
    const baseSection = this.querySelector('[data-base-section]');
    if (!baseSection) return;

    const baseCards = Array.from(baseSection.querySelectorAll('.bundle-charm-card--base'));
    if (baseCards.length === 0) return;

    if (metal === 'both') {
      // Pick the metal that matches the majority of recommended charms
      let goldCount = 0;
      let silverCount = 0;
      for (const charm of this.state.charms) {
        const vt = (charm.variantTitle || charm.title || '').toLowerCase();
        if (vt.includes('gold')) goldCount++;
        if (vt.includes('silver')) silverCount++;
      }
      const bestMetal = silverCount > goldCount ? 'silver' : 'gold';
      const card = baseCards.find(c => (c.dataset.variantTitle || '').toLowerCase().includes(bestMetal)) || baseCards[0];
      if (card) {
        const variantId = parseInt(card.dataset.variantId, 10);
        const alreadyAdded = this.state.bases.find(b => b.variantId === variantId);
        if (!alreadyAdded) {
          this.addBaseFromGrid(card);
        }
      }
    } else {
      // Single metal — find the matching base card
      const card = baseCards.find(c => {
        const vt = (c.dataset.variantTitle || '').toLowerCase();
        return vt.includes(metal);
      }) || baseCards[0];

      if (card) {
        const variantId = parseInt(card.dataset.variantId, 10);
        const alreadyAdded = this.state.bases.find(b => b.variantId === variantId);
        if (!alreadyAdded) {
          this.addBaseFromGrid(card);
        }
      }
    }
  }

  /** Vibe-to-category mapping */
  static get VIBE_CATEGORIES() {
    return {
      love: ['love'],
      nature: ['nature'],
      spiritual: ['spiritual'],
      fun: ['bears'],
      sparkle: ['love', 'nature', 'spiritual', 'bears']
    };
  }

  /* ---------------------------------------------------------------
   * Quiz Picks Strip — fetches product data from Shopify API
   * and renders a row of small cards above the browse grid.
   * --------------------------------------------------------------- */

  /** Fetch product JSON from Shopify storefront AJAX API */
  async fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.json`);
      if (!res.ok) return null;
      const data = await res.json();
      return data.product;
    } catch (e) {
      console.warn('Failed to fetch product:', handle, e);
      return null;
    }
  }

  /**
   * Find a variant in a Shopify product object by matching a target string
   * against variant titles. Handles compound titles like "Gold / M".
   */
  findVariant(product, target, metalHint) {
    if (!product || !product.variants) return null;
    const t = target.trim().toLowerCase();
    const m = (metalHint || '').toLowerCase();

    // Try exact match first
    for (const v of product.variants) {
      if (v.title.trim().toLowerCase() === t) return v;
    }

    // Try "Metal / Target" compound match
    if (m) {
      for (const v of product.variants) {
        const title = v.title.toLowerCase();
        if (title.includes(m) && title.includes(t)) return v;
      }
    }

    // Try after-slash match: "Gold / M" → check if part after "/" matches
    for (const v of product.variants) {
      const parts = v.title.split('/').map(p => p.trim().toLowerCase());
      if (parts.length > 1 && parts[parts.length - 1] === t) return v;
    }

    // Loose includes
    for (const v of product.variants) {
      if (v.title.toLowerCase().includes(t)) return v;
    }

    return product.variants[0] || null;
  }

  /**
   * Render the "Your Picks" strip with The Ends base + quiz-recommended charms.
   * Each pick is fetched from /products/{handle}.json — no DOM cloning.
   */
  async renderQuizPicks(recommendations, metal) {
    const picksContainer = this.querySelector('[data-quiz-picks]');
    const picksGrid = this.querySelector('[data-quiz-picks-grid]');
    const subtitle = this.querySelector('[data-quiz-picks-subtitle]');
    if (!picksContainer || !picksGrid) return;

    // Clear previous picks
    picksGrid.innerHTML = '';
    this.state.quizPicks = []; // Track picks separately from grid charms

    // Collect all handles we need to fetch (deduplicate)
    const fetchMap = new Map(); // handle → [{ target, label }]
    const { zodiacHandle, zodiacVariant, letterSelections, numberSelections } = recommendations;

    if (zodiacHandle) {
      if (!fetchMap.has(zodiacHandle)) fetchMap.set(zodiacHandle, []);
      fetchMap.get(zodiacHandle).push({ target: zodiacVariant, label: 'zodiac' });
    }
    for (const sel of (letterSelections || [])) {
      if (!fetchMap.has(sel.handle)) fetchMap.set(sel.handle, []);
      fetchMap.get(sel.handle).push({ target: sel.variant, label: 'letter' });
    }
    for (const sel of (numberSelections || [])) {
      if (!fetchMap.has(sel.handle)) fetchMap.set(sel.handle, []);
      fetchMap.get(sel.handle).push({ target: sel.variant, label: 'number' });
    }

    // Fetch all products in parallel
    const handles = [...fetchMap.keys()];
    const products = await Promise.all(handles.map(h => this.fetchProduct(h)));

    // Build product lookup
    const productMap = {};
    handles.forEach((h, i) => { if (products[i]) productMap[h] = products[i]; });

    // Render each quiz pick (no bases — they have their own section)
    for (const [handle, targets] of fetchMap) {
      const product = productMap[handle];
      if (!product) continue;

      for (const { target, label } of targets) {
        const variant = this.findVariant(product, target, metal);
        if (!variant) continue;

        // Look up variant-specific image from product.images using image_id
        let image = '';
        if (variant.image_id && product.images) {
          const variantImage = product.images.find(img => img.id === variant.image_id);
          if (variantImage) image = variantImage.src;
        }
        if (!image && variant.featured_image) image = variant.featured_image.src;
        if (!image && product.image) image = product.image.src;
        if (!image && product.images && product.images[0]) image = product.images[0].src;

        const pickData = {
          variantId: variant.id,
          productId: product.id,
          title: product.title,
          variantTitle: variant.title,
          price: Math.round(parseFloat(variant.price) * 100),
          image: image,
          handle: handle,
          quizLabel: label
        };

        // Render the card
        const cardHtml = this.renderPickCard({
          image: image,
          title: this.getPickDisplayTitle(label, target, product.title),
          variantTitle: variant.title,
          price: pickData.price,
          idx: this.state.quizPicks.length,
          handle: handle
        });
        picksGrid.insertAdjacentHTML('beforeend', cardHtml);

        // Track in state
        this.state.quizPicks.push(pickData);
        // Also add to charms so they go to cart
        this.state.charms.push({
          variantId: variant.id,
          productId: product.id,
          title: product.title,
          variantTitle: variant.title,
          price: pickData.price,
          image: image,
          handle: handle,
          cardEl: null, // no DOM card in the grid — lives in picks strip
          isQuizPick: true,
          quantity: 1
        });
      }
    }

    // Bind remove buttons on pick cards
    picksGrid.querySelectorAll('[data-remove-pick]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.removePick, 10);
        this.removeQuizPick(idx);
      });
    });

    // Bind pick card clicks to open charm detail modal
    picksGrid.querySelectorAll('.quiz-pick-card[data-handle]').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-remove-pick]')) return;
        const hdl = card.dataset.handle;
        if (!hdl) return;
        const gridCard = this.querySelector(`.bundle-charm-card[data-handle="${hdl}"]`);
        if (gridCard) this.openCharmModal(gridCard);
      });
    });

    // Show the strip
    picksContainer.hidden = false;
    if (subtitle) {
      subtitle.textContent = `${this.state.quizPicks.length} charms + The Ends`;
    }

    this.updateSummaryBar();
  }

  /** Short display title for a pick card */
  getPickDisplayTitle(label, target, productTitle) {
    if (label === 'zodiac') return target;
    if (label === 'letter') return `Letter ${target}`;
    if (label === 'number') return `Number ${target}`;
    return productTitle;
  }

  /** Render HTML for a single pick card in the strip */
  renderPickCard({ image, title, variantTitle, price, isBase, idx, source, quantity, variantId, handle }) {
    const priceStr = this.formatMoney(price);
    const vidAttr = variantId ? ` data-variant-id="${variantId}"` : '';
    const handleAttr = handle ? ` data-handle="${this.escapeHtml(handle)}"` : '';
    const baseAttr = isBase ? ' data-is-base="true"' : '';
    const removeBtn = isBase ? '' : `<button type="button" class="quiz-pick-card__remove" data-remove-pick="${idx}" data-pick-source="${source || 'quiz'}"${vidAttr}>&times;</button>`;
    const badgeClass = isBase ? 'quiz-pick-card--base' : '';
    const qty = quantity || 1;
    const qtyBadge = qty > 1 ? `<div class="quiz-pick-card__qty">x${qty}</div>` : '';

    return `
      <div class="quiz-pick-card ${badgeClass}"${handleAttr}${baseAttr} style="cursor:pointer">
        ${removeBtn}
        ${qtyBadge}
        <img class="quiz-pick-card__image" src="${image}" alt="${this.escapeHtml(title)}" width="120" height="120" loading="lazy">
        <div class="quiz-pick-card__title">${this.escapeHtml(title)}</div>
        <div class="quiz-pick-card__price">${priceStr}</div>
      </div>
    `;
  }

  /** Remove a quiz pick by index */
  removeQuizPick(idx) {
    if (idx < 0 || idx >= this.state.quizPicks.length) return;
    const pick = this.state.quizPicks[idx];

    // Remove from charms state
    this.state.charms = this.state.charms.filter(c =>
      !(c.isQuizPick && c.variantId === pick.variantId && c.handle === pick.handle)
    );

    // Remove from quizPicks
    this.state.quizPicks.splice(idx, 1);

    // Re-render the picks strip
    this.reRenderPicksStrip();
    this.updateSummaryBar();
  }

  /**
   * Re-render the entire picks strip: base + quiz picks + grid-added charms.
   * Called after any add/remove from quiz picks or grid sync.
   */
  reRenderPicksStrip() {
    const picksGrid = this.querySelector('[data-quiz-picks-grid]');
    const subtitle = this.querySelector('[data-quiz-picks-subtitle]');
    if (!picksGrid) return;

    picksGrid.textContent = '';

    // Render The Ends base items first
    for (const base of this.state.bases) {
      picksGrid.insertAdjacentHTML('beforeend', this.renderPickCard({
        image: base.image,
        title: 'The Ends',
        variantTitle: base.variantTitle + (base.quantity > 1 ? ` x${base.quantity}` : ''),
        price: base.price * base.quantity,
        isBase: true,
        quantity: base.quantity
      }));
    }

    // Merge quiz picks + grid charms by variantId to avoid duplicates
    const mergedMap = new Map();

    (this.state.quizPicks || []).forEach((pick) => {
      const vid = pick.variantId;
      if (mergedMap.has(vid)) {
        mergedMap.get(vid).quantity += 1;
      } else {
        mergedMap.set(vid, {
          variantId: vid,
          image: pick.image,
          title: this.getPickDisplayTitle(pick.quizLabel, pick.variantTitle.split('/').pop().trim(), pick.title),
          variantTitle: pick.variantTitle,
          price: pick.price,
          handle: pick.handle,
          quantity: 1
        });
      }
    });

    const gridCharms = this.state.charms.filter(c => !c.isQuizPick && (c.cardEl || c._groupCardEl) && !c.cardEl?.dataset?.isBase);
    gridCharms.forEach((charm) => {
      const vid = charm.variantId;
      const qty = charm.quantity || 1;
      if (mergedMap.has(vid)) {
        mergedMap.get(vid).quantity += qty;
      } else {
        let displayTitle = charm.title;
        if (charm.variantTitle && charm.variantTitle !== 'Default Title') {
          displayTitle += ' - ' + charm.variantTitle;
        }
        mergedMap.set(vid, {
          variantId: vid,
          image: charm.image,
          title: displayTitle,
          variantTitle: charm.variantTitle,
          price: charm.price,
          handle: charm.handle,
          quantity: qty
        });
      }
    });

    // Render merged list
    let pickIndex = 0;
    for (const [vid, item] of mergedMap) {
      const idx = pickIndex++;
      picksGrid.insertAdjacentHTML('beforeend', this.renderPickCard({
        image: item.image,
        title: item.title,
        variantTitle: item.variantTitle,
        price: item.price * item.quantity,
        idx: idx,
        source: 'merged',
        quantity: item.quantity,
        variantId: vid,
        handle: item.handle
      }));
    }

    // Bind remove buttons
    picksGrid.querySelectorAll('[data-remove-pick]').forEach(btn => {
      btn.addEventListener('click', () => {
        const vid = parseInt(btn.dataset.variantId, 10);
        this.removeMergedPick(vid);
      });
    });

    // Bind pick card clicks — base cards scroll to section, charm cards open modal
    picksGrid.querySelectorAll('.quiz-pick-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-remove-pick]')) return;
        if (card.dataset.isBase) {
          const baseSection = this.querySelector('[data-base-section]');
          if (baseSection) baseSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        const handle = card.dataset.handle;
        if (!handle) return;
        const gridCard = this.querySelector(`.bundle-charm-card[data-handle="${handle}"]`);
        if (gridCard) this.openCharmModal(gridCard);
      });
    });

    // Update subtitle
    const totalCharms = mergedMap.size;
    const totalBases = this.state.bases.reduce((sum, b) => sum + (b.quantity || 1), 0);
    const totalItems = totalCharms + totalBases;
    if (subtitle) {
      subtitle.textContent = totalItems > 0
        ? `${totalItems} item${totalItems !== 1 ? 's' : ''}`
        : 'Add items below';
    }
  }

  /** Remove all state entries (quiz picks + grid charms) for a given variantId */
  removeMergedPick(variantId) {
    if (this.state.quizPicks) {
      this.state.quizPicks = this.state.quizPicks.filter(p => p.variantId !== variantId);
    }

    const toRemove = this.state.charms.filter(c => c.variantId === variantId);
    this.state.charms = this.state.charms.filter(c => c.variantId !== variantId);

    // GA4 remove_from_cart for user-initiated removal
    if (toRemove.length > 0) {
      const removeValue = toRemove.reduce((sum, c) => sum + (c.price || 0) * (c.quantity || 1), 0);
      this.pushDataLayer('remove_from_cart', {
        value: this.centsToDecimal(removeValue),
        items: toRemove.map(c => ({
          item_id: String(c.productId),
          item_name: c.title,
          item_variant: c.variantTitle || '',
          price: this.centsToDecimal(c.price || 0),
          quantity: c.quantity || 1
        }))
      });
    }

    for (const charm of toRemove) {
      if (charm.cardEl) {
        const anyLeft = this.state.charms.some(c => c.cardEl === charm.cardEl);
        if (!anyLeft) charm.cardEl.classList.remove('is-selected');
        this.updateCardQuantityUI(charm.cardEl);
      }
      if (charm._groupCardEl) {
        this.updateGroupCardBadge(charm._groupCardEl);
      }
    }

    this.reRenderPicksStrip();
    this.updateSummaryBar();
  }

  /** Sync the picks strip after a grid toggle (add or remove) */
  syncPicksStrip() {
    const picksContainer = this.querySelector('[data-quiz-picks]');
    if (!picksContainer) return;
    const hasItems = this.state.bases.length > 0 || this.state.charms.length > 0;
    picksContainer.hidden = !hasItems;
    if (!hasItems) return;
    this.reRenderPicksStrip();
  }

  /** Remove a grid-added charm from the picks strip (also deselects it in the grid) */
  removeGridPick(pickIdx) {
    // pickIdx is relative to the full picks list, grid items start after quiz picks
    const quizCount = (this.state.quizPicks || []).length;
    const gridIdx = pickIdx - quizCount;
    const gridCharms = this.state.charms.filter(c => !c.isQuizPick && (c.cardEl || c._groupCardEl));

    if (gridIdx < 0 || gridIdx >= gridCharms.length) return;
    const charm = gridCharms[gridIdx];

    // Remove from state
    const cardEl = charm.cardEl;
    const groupCard = charm._groupCardEl;
    this.state.charms = this.state.charms.filter(c => c !== charm);

    // Only deselect the grid card if no other variant entries remain
    if (cardEl) {
      const anyLeft = this.state.charms.some(c => c.cardEl === cardEl);
      if (!anyLeft) cardEl.classList.remove('is-selected');
      this.updateCardQuantityUI(cardEl);
    }

    // Update group card badge if this was from a group card
    if (groupCard) this.updateGroupCardBadge(groupCard);

    this.reRenderPicksStrip();
    this.updateSummaryBar();
  }

  /* ---------------------------------------------------------------
   * Grid Filtering (browse section below picks)
   * --------------------------------------------------------------- */

  applyQuizFiltersToGrid(recommendations, metal) {
    const { vibeCategories, personalityHandles } = recommendations;
    const MIN_EXTRA_CHARMS = 20;

    // Personality match handles get priority visibility
    const priorityHandles = new Set(personalityHandles || []);

    // Pass 1: classify every card into tiers
    const tier1 = []; // curated: vibe+metal match or personality match
    const tier2 = []; // suggested: metal match but different vibe
    const tier3 = []; // remaining: everything else

    this.querySelectorAll('.bundle-charm-card').forEach(card => {
      // Never filter The Ends — they must always stay visible
      if (card.classList.contains('bundle-charm-card--base') || card.dataset.isBase) return;

      const handle = card.dataset.handle || '';
      const cardCategories = (card.dataset.categories || '').split(' ');
      const cardMetals = (card.dataset.metals || '').split(' ');

      const isPriority = priorityHandles.has(handle);
      const vibeMatch = (vibeCategories || []).some(cat => cardCategories.includes(cat));
      const metalMatch = metal === 'both' || cardMetals.includes(metal);

      if (isPriority || (vibeMatch && metalMatch)) {
        tier1.push(card);
      } else if (metalMatch) {
        tier2.push(card);
      } else {
        tier3.push(card);
      }
    });

    // Pass 2: show tier 1 cards
    let visibleCount = 0;
    for (const card of tier1) {
      card.hidden = false;
      card.removeAttribute('data-suggested');
      visibleCount++;
      if (metal !== 'both') this.autoSelectMetalVariant(card, metal);
    }

    // Pass 3: if tier 1 has fewer than MIN_EXTRA_CHARMS, show tier 2 cards
    const needMore = MIN_EXTRA_CHARMS - visibleCount;
    let suggestedCount = 0;

    if (needMore > 0) {
      for (const card of tier2) {
        card.hidden = false;
        card.dataset.suggested = 'true';
        suggestedCount++;
        visibleCount++;
        if (metal !== 'both') this.autoSelectMetalVariant(card, metal);
        if (suggestedCount >= needMore && visibleCount >= MIN_EXTRA_CHARMS) break;
      }
    }

    // If we still haven't hit 20 from tier2, pull from tier3
    if (visibleCount < MIN_EXTRA_CHARMS) {
      for (const card of tier3) {
        card.hidden = false;
        card.dataset.suggested = 'true';
        suggestedCount++;
        visibleCount++;
        if (visibleCount >= MIN_EXTRA_CHARMS) break;
      }
    }

    // Hide remaining cards that weren't shown
    for (const card of tier2) { if (!card.dataset.suggested && !tier1.includes(card)) card.hidden = true; }
    for (const card of tier3) { if (!card.dataset.suggested) card.hidden = true; }

    this.pushCustomEvent('quiz_results_viewed', {
      result_count: visibleCount,
      curated_count: tier1.length,
      suggested_count: suggestedCount
    });
  }

  autoSelectMetalVariant(card, metal) {
    const variants = card.querySelectorAll('.bundle-charm-variant');
    if (variants.length <= 1) return;

    for (const btn of variants) {
      const title = (btn.dataset.variantTitle || '').toLowerCase();
      if (title.includes(metal)) {
        if (!btn.classList.contains('is-active')) {
          // Activate variant visually
          card.querySelectorAll('.bundle-charm-variant').forEach(v => v.classList.remove('is-active'));
          btn.classList.add('is-active');
          if (btn.dataset.variantId) card.dataset.variantId = btn.dataset.variantId;
          if (btn.dataset.price) card.dataset.price = btn.dataset.price;
          if (btn.dataset.image) {
            this.swapCardImage(card, btn.dataset.image);
          }
        }
        break;
      }
    }
  }

  showCuratedBanner(vibes, zodiac, metal) {
    const banner = this.querySelector('[data-curated-banner]');
    const label = this.querySelector('[data-curated-label]');
    if (!banner || !label) return;

    const vibeNames = {
      love: 'Love & Romance',
      nature: 'Nature & Earth',
      spiritual: 'Spiritual & Mystical',
      fun: 'Fun & Playful',
      sparkle: 'Sparkle & Shine'
    };

    const metalName = metal === 'both' ? 'Gold & Silver' : metal.charAt(0).toUpperCase() + metal.slice(1);
    const vibeLabels = vibes.map(v => vibeNames[v] || v).join(' + ');
    const zodiacName = zodiac ? zodiac.charAt(0).toUpperCase() + zodiac.slice(1) : '';

    label.textContent = zodiacName
      ? `${zodiacName} \u00b7 ${vibeLabels} \u00b7 ${metalName}`
      : `${vibeLabels} \u00b7 ${metalName}`;
    banner.hidden = false;
  }

  clearQuizFilters() {
    this.state.quizFilters = null;

    // Show all charms and clean up quiz-related attributes
    this.querySelectorAll('.bundle-charm-card').forEach(card => {
      card.hidden = false;
      card.removeAttribute('data-suggested');
    });

    // Hide banner
    const banner = this.querySelector('[data-curated-banner]');
    if (banner) banner.hidden = true;

    // Show filter tabs
    const filtersWrap = this.querySelector('[data-filters-wrap]');
    if (filtersWrap) filtersWrap.hidden = false;

    // Reset filter state
    this.setFilter('all');

    // Clear persisted quiz state
    try { sessionStorage.removeItem('theends_quiz'); } catch (e) { /* ignore */ }

    // Re-sync picks strip — bases are still selected, so show them
    this.syncPicksStrip();
    this.updateSummaryBar();

    // GA4
    this.pushCustomEvent('quiz_filters_cleared', {});
  }

  /* ---------------------------------------------------------------
   * Letter/Number Picker Modal
   * --------------------------------------------------------------- */

  async openLetterModal(groupCard) {
    const overlay = document.querySelector('[data-letter-modal-overlay]');
    const grid = document.querySelector('[data-letter-modal-grid]');
    const title = document.querySelector('[data-letter-modal-title]');
    const subtitle = document.querySelector('[data-letter-modal-subtitle]');
    const countEl = document.querySelector('[data-letter-modal-count]');
    const confirmBtn = document.querySelector('[data-action="confirm-letter-modal"]');
    if (!overlay || !grid) return;

    const groupType = groupCard.dataset.groupType || 'letters';
    const isEnamelSuper = !!groupCard.dataset.enamelSuper;

    // Enamel super-group: fetch product data for each enamel color
    let enamelProducts = null;
    let variants = [];

    if (isEnamelSuper) {
      const handles = (groupCard.dataset.enamelHandles || '').split('|').filter(Boolean);
      enamelProducts = [];
      for (const h of handles) {
        const prod = await this.fetchProduct(h);
        if (prod) enamelProducts.push(prod);
      }
      if (enamelProducts.length === 0) return;

      // Extract color names from product titles (e.g., "Pink & Gold..." -> "Pink")
      enamelProducts.forEach(p => {
        const match = p.title.match(/^(\w[\w\s]*?)\s*&\s*(Gold|Silver)/i);
        p._enamelColor = match ? match[1].trim() : p.title.split(' ')[0];
      });

      // Default to first color
      const defaultProduct = enamelProducts[0];
      variants = defaultProduct.variants.filter(v => v.available !== false).map(v => {
        const parts = v.title.split(' / ');
        return {
          id: v.id,
          title: v.title,
          label: parts.length > 1 ? parts[parts.length - 1].trim() : v.title,
          price: Math.round(parseFloat(v.price) * 100),
          image: this.getVariantImage(defaultProduct, v) || (defaultProduct.images?.[0]?.src) || '',
          available: true
        };
      });
    } else if (groupCard.dataset.variants) {
      // Standard group card: parse variants from data attribute
      try {
        variants = JSON.parse(groupCard.dataset.variants);
      } catch (e) {
        console.warn('Failed to parse group card variants:', e);
        return;
      }
    } else if (groupCard.dataset.handle) {
      // Fetch product by handle (e.g. sorority cards without inline variant JSON)
      const prod = await this.fetchProduct(groupCard.dataset.handle);
      if (!prod || !prod.variants) return;
      variants = prod.variants.filter(v => v.available !== false).map(v => {
        const parts = v.title.split(' / ');
        return {
          id: v.id,
          title: v.title,
          label: parts.length > 1 ? parts[parts.length - 1].trim() : v.title,
          price: Math.round(parseFloat(v.price) * 100),
          image: this.getVariantImage(prod, v) || (prod.images?.[0]?.src) || '',
          available: true
        };
      });
    }

    const styleName = groupCard.dataset.style
      ? groupCard.dataset.style.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : '';

    // Detect color dimension from variant titles (for non-enamel multi-color products)
    const colors = isEnamelSuper ? [] : [...new Set(variants.map(v => {
      const parts = v.title.split(' / ');
      return parts.length > 1 ? parts[0].trim() : null;
    }).filter(Boolean))];
    const hasColorPicker = colors.length > 1;

    // Store modal state
    this._modalState = {
      groupCard,
      variants,
      selected: new Set(),
      groupType,
      productId: parseInt(groupCard.dataset.productId, 10),
      handle: groupCard.dataset.handle,
      productTitle: groupCard.dataset.productTitle,
      colors,
      hasColorPicker,
      selectedColor: hasColorPicker ? colors[0] : null,
      isEnamelSuper,
      enamelProducts: enamelProducts || null,
      activeEnamelProduct: enamelProducts ? enamelProducts[0] : null
    };

    // Pre-select variants that are already in the charm state for this group card
    this.state.charms.forEach(charm => {
      if (charm._groupCardEl === groupCard) {
        this._modalState.selected.add(charm.variantId);
      }
    });

    // Update header
    const isSorority = (groupCard.dataset.productTitle || '').toLowerCase().includes('sorority');
    if (title) title.textContent = isSorority ? 'Pick Your Sorority' : `Pick Your ${groupType === 'letters' ? 'Letters' : 'Numbers'}`;
    if (subtitle) {
      if (isEnamelSuper) {
        subtitle.textContent = 'Pick a color, then select your letters';
      } else {
        subtitle.textContent = styleName ? `${styleName} style` : 'Tap to select, then confirm';
      }
    }

    // Render enamel color picker or standard color picker
    if (isEnamelSuper) {
      this.renderEnamelColorPicker(grid);
    } else {
      this.renderLetterModalColorPicker(grid);
    }

    // Render tile grid
    this.renderLetterModalTiles(grid);

    // Update count and show
    this.updateLetterModalCount();
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';

    // Close on overlay click (not modal content)
    this._modalOverlayHandler = (e) => {
      if (e.target === overlay) this.closeLetterModal();
    };
    overlay.addEventListener('click', this._modalOverlayHandler);

    // Close on Escape
    this._modalEscHandler = (e) => { if (e.key === 'Escape') this.closeLetterModal(); };
    document.addEventListener('keydown', this._modalEscHandler);
  }

  /** Render enamel color picker (product-level colors, not variant-level) */
  renderEnamelColorPicker(grid) {
    const existing = grid.parentElement.querySelector('.bundle-letter-modal__colors');
    if (existing) existing.remove();

    const { enamelProducts, activeEnamelProduct } = this._modalState;
    if (!enamelProducts || enamelProducts.length <= 1) return;

    const colorRow = document.createElement('div');
    colorRow.className = 'bundle-letter-modal__colors';

    const enamelColorMap = {
      'pink': '#FF69B4',
      'purple': '#9B59B6',
      'dark purple': '#4A0E4E',
      'orange': '#FF6B35',
      'white': '#FFFFFF'
    };

    enamelProducts.forEach(prod => {
      const colorName = (prod._enamelColor || '').toLowerCase();
      const bgColor = enamelColorMap[colorName] || '#ccc';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-letter-modal__color-btn bundle-letter-modal__color-btn--circle' + (prod === activeEnamelProduct ? ' is-active' : '');
      btn.title = prod._enamelColor;
      btn.dataset.handle = prod.handle;
      btn.dataset.enamelColor = colorName;
      btn.style.background = bgColor;
      if (colorName === 'white') btn.style.border = '2px solid #ccc';
      btn.addEventListener('click', () => {
        this._modalState.activeEnamelProduct = prod;
        this._modalState.handle = prod.handle;
        this._modalState.productId = prod.id;
        this._modalState.productTitle = prod.title;

        // Rebuild variants from the selected enamel product
        this._modalState.variants = prod.variants.filter(v => v.available !== false).map(v => {
          const parts = v.title.split(' / ');
          return {
            id: v.id,
            title: v.title,
            label: parts.length > 1 ? parts[parts.length - 1].trim() : v.title,
            price: Math.round(parseFloat(v.price) * 100),
            image: this.getVariantImage(prod, v) || (prod.images?.[0]?.src) || '',
            available: true
          };
        });

        // Update active button
        colorRow.querySelectorAll('.bundle-letter-modal__color-btn').forEach(b => {
          b.classList.toggle('is-active', b.dataset.handle === prod.handle);
        });

        // Re-render tiles with new product's variants
        this.renderLetterModalTiles(grid);
        this.updateLetterModalCount();
      });
      colorRow.appendChild(btn);
    });

    grid.parentElement.insertBefore(colorRow, grid);
  }

  /** Render color swatch row above the letter/number grid in the modal */
  renderLetterModalColorPicker(grid) {
    // Remove existing color picker
    const existing = grid.parentElement.querySelector('.bundle-letter-modal__colors');
    if (existing) existing.remove();

    if (!this._modalState.hasColorPicker) return;

    const { colors, selectedColor } = this._modalState;
    const colorRow = document.createElement('div');
    colorRow.className = 'bundle-letter-modal__colors';

    colors.forEach(color => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-letter-modal__color-btn' + (color === selectedColor ? ' is-active' : '');
      btn.textContent = color;
      btn.dataset.color = color;
      btn.addEventListener('click', () => {
        this._modalState.selectedColor = color;
        colorRow.querySelectorAll('.bundle-letter-modal__color-btn').forEach(b => {
          b.classList.toggle('is-active', b.dataset.color === color);
        });
        this.renderLetterModalTiles(grid);
      });
      colorRow.appendChild(btn);
    });

    grid.parentElement.insertBefore(colorRow, grid);
  }

  /** Render letter/number selection as text grid + chips (no images) */
  renderLetterModalTiles(grid) {
    const { variants, selected, hasColorPicker, selectedColor, groupType } = this._modalState;

    // Filter variants by color if color picker is active (non-enamel products with Gold/Silver variants)
    const filtered = hasColorPicker
      ? variants.filter(v => v.title.startsWith(selectedColor + ' / '))
      : variants;

    grid.textContent = '';

    // Switch to long-label list layout if any label is longer than 3 chars
    const hasLongLabels = filtered.some(v => (v.label || '').length > 3);
    grid.classList.toggle('bundle-letter-modal__grid--long-labels', hasLongLabels);

    // Chips container (shows selected items as removable tags)
    let chipsWrap = grid.parentElement.querySelector('.bundle-letter-modal__chips');
    if (!chipsWrap) {
      chipsWrap = document.createElement('div');
      chipsWrap.className = 'bundle-letter-modal__chips';
      grid.parentElement.insertBefore(chipsWrap, grid.nextSibling);
    }

    // Text button grid (A-Z or 0-9, or list for long labels)
    filtered.forEach(v => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-letter-btn' + (selected.has(v.id) ? ' is-selected' : '');
      btn.dataset.variantId = String(v.id);
      btn.dataset.variantTitle = v.title;
      btn.dataset.variantLabel = v.label;
      btn.dataset.price = String(v.price);
      btn.dataset.image = v.image || '';
      btn.textContent = v.label;

      btn.addEventListener('click', () => {
        this.toggleLetterInModal(btn);
        this.renderLetterChips(chipsWrap);
      });

      grid.appendChild(btn);
    });

    // Render chips for currently selected items
    this.renderLetterChips(chipsWrap);
  }

  /** Render selected letter/number chips below the grid */
  renderLetterChips(chipsWrap) {
    if (!chipsWrap) return;
    chipsWrap.textContent = '';

    const { selected, variants, isEnamelSuper, _selectedDetails } = this._modalState;
    if (selected.size === 0) {
      chipsWrap.hidden = true;
      return;
    }
    chipsWrap.hidden = false;

    // For enamel super-groups, look up details from _selectedDetails map
    // For standard groups, look up from variants array
    const enamelColorMap = {
      'pink': '#FF69B4',
      'purple': '#9B59B6',
      'dark purple': '#4A0E4E',
      'orange': '#FF6B35',
      'white': '#FFFFFF'
    };

    for (const variantId of selected) {
      let label = '';
      let colorPrefix = '';
      let dotColor = '';

      if (isEnamelSuper && _selectedDetails && _selectedDetails.has(variantId)) {
        const detail = _selectedDetails.get(variantId);
        const parts = detail.variantTitle.split(' / ');
        label = parts.length > 1 ? parts[parts.length - 1].trim() : detail.variantTitle;
        const prodMatch = detail.productTitle.match(/^(\w[\w\s]*?)\s*&/i);
        colorPrefix = prodMatch ? prodMatch[1].trim() + ' ' : '';
        dotColor = enamelColorMap[detail.enamelColor] || '';
      } else {
        const v = variants.find(vr => vr.id === variantId);
        label = v ? v.label : String(variantId);
      }

      const dotHtml = dotColor
        ? `<span class="bundle-letter-chip__dot" style="background:${dotColor};${dotColor === '#FFFFFF' ? 'border:1px solid #ccc;' : ''}"></span>`
        : '';

      const chip = document.createElement('span');
      chip.className = 'bundle-letter-chip';
      chip.innerHTML = `${dotHtml}${colorPrefix}${label} <button type="button" class="bundle-letter-chip__remove" data-remove-variant="${variantId}">&times;</button>`;
      chip.querySelector('.bundle-letter-chip__remove').addEventListener('click', () => {
        selected.delete(variantId);
        if (_selectedDetails) _selectedDetails.delete(variantId);
        // Update the grid button state
        const gridBtn = this.querySelector(`[data-letter-modal-grid] .bundle-letter-btn[data-variant-id="${variantId}"]`);
        if (gridBtn) gridBtn.classList.remove('is-selected');
        this.renderLetterChips(chipsWrap);
        this.updateLetterModalCount();
      });
      chipsWrap.appendChild(chip);
    }
  }

  toggleLetterInModal(tile) {
    const variantId = parseInt(tile.dataset.variantId, 10);
    if (this._modalState.selected.has(variantId)) {
      this._modalState.selected.delete(variantId);
      if (this._modalState._selectedDetails) this._modalState._selectedDetails.delete(variantId);
      tile.classList.remove('is-selected');
    } else {
      this._modalState.selected.add(variantId);
      // For enamel super-groups, store full product context for each selected variant
      if (this._modalState.isEnamelSuper) {
        if (!this._modalState._selectedDetails) this._modalState._selectedDetails = new Map();
        const activeProd = this._modalState.activeEnamelProduct;
        this._modalState._selectedDetails.set(variantId, {
          variantId,
          productId: this._modalState.productId,
          handle: this._modalState.handle,
          productTitle: this._modalState.productTitle,
          variantTitle: tile.dataset.variantTitle || '',
          price: parseInt(tile.dataset.price, 10) || 0,
          image: tile.dataset.image || '',
          enamelColor: activeProd ? (activeProd._enamelColor || '').toLowerCase() : ''
        });
      }
      tile.classList.add('is-selected');
    }
    this.updateLetterModalCount();
  }

  updateLetterModalCount() {
    const countEl = document.querySelector('[data-letter-modal-count]');
    const confirmBtn = document.querySelector('[data-action="confirm-letter-modal"]');
    const count = this._modalState ? this._modalState.selected.size : 0;
    if (countEl) countEl.textContent = `${count} selected`;
    if (confirmBtn) {
      confirmBtn.disabled = count === 0;
      confirmBtn.textContent = count > 0 ? `Add ${count} to Set` : 'Add to Set';
    }
  }

  confirmLetterModal() {
    if (!this._modalState || this._modalState.selected.size === 0) return;

    const { groupCard, selected, isEnamelSuper } = this._modalState;

    // Remove any existing selections from this group card
    this.state.charms = this.state.charms.filter(c => c._groupCardEl !== groupCard);

    // For enamel super-groups, selections are stored as {variantId, productId, handle, title, ...} objects
    // For standard groups, they're just variant IDs and we use the single product context
    if (isEnamelSuper && this._modalState._selectedDetails) {
      for (const [variantId, detail] of this._modalState._selectedDetails) {
        this.state.charms.push({
          variantId: detail.variantId,
          productId: detail.productId,
          title: detail.productTitle,
          variantTitle: detail.variantTitle,
          price: detail.price,
          image: detail.image || groupCard.dataset.image,
          handle: detail.handle,
          cardEl: null,
          _groupCardEl: groupCard,
          quantity: 1
        });
      }
    } else {
      const { variants, productId, handle, productTitle } = this._modalState;
      for (const variantId of selected) {
        const v = variants.find(vr => vr.id === variantId);
        if (!v) continue;
        this.state.charms.push({
          variantId: v.id,
          productId,
          title: productTitle,
          variantTitle: v.title,
          price: v.price,
          image: v.image || groupCard.dataset.image,
          handle,
          cardEl: null,
          _groupCardEl: groupCard,
          quantity: 1
        });
      }
    }

    // Update the group card badge
    this.updateGroupCardBadge(groupCard);

    // Close modal
    this.closeLetterModal();

    // Update state
    this.syncPicksStrip();
    this.updateSummaryBar();

    // GA4
    const allItems = this.state.charms.filter(c => c._groupCardEl === groupCard);
    this.pushDataLayer('select_item', {
      items: allItems.map(c => ({
        item_id: String(c.productId),
        item_name: c.title,
        item_variant: c.variantTitle || '',
        price: this.centsToDecimal(c.price)
      }))
    });
  }

  closeLetterModal() {
    const overlay = document.querySelector('[data-letter-modal-overlay]');
    if (overlay) {
      overlay.hidden = true;
      if (this._modalOverlayHandler) {
        overlay.removeEventListener('click', this._modalOverlayHandler);
        this._modalOverlayHandler = null;
      }
      // Clean up chips and color picker
      const chips = overlay.querySelector('.bundle-letter-modal__chips');
      if (chips) chips.remove();
      const colors = overlay.querySelector('.bundle-letter-modal__colors');
      if (colors) colors.remove();
    }
    document.body.style.overflow = '';
    if (this._modalEscHandler) {
      document.removeEventListener('keydown', this._modalEscHandler);
      this._modalEscHandler = null;
    }
  }

  /* ---------------------------------------------------------------
   * Charm Detail Modal
   * --------------------------------------------------------------- */

  async openCharmModal(card) {
    const handle = card.dataset.handle;
    const overlay = this.querySelector('[data-charm-modal-overlay]');
    if (!overlay || !handle) return;

    // Fetch full product data
    const product = await this.fetchProduct(handle);
    if (!product) { this.increaseCharmOnCard(card); return; } // fallback to quick-add

    const currentVariantId = card.dataset.variantId;
    const currentVariant = product.variants.find(v => String(v.id) === String(currentVariantId)) || product.variants[0];

    // Build per-variant qty map, pre-populated from existing state
    const isBaseCard = card.classList.contains('bundle-charm-card--base') || card.dataset.isBase === 'true';
    const variantQtys = {};
    const availableVars = product.variants.filter(v => v.available !== false);
    for (const v of availableVars) {
      const vid = String(v.id);
      if (isBaseCard) {
        // Base products use state.bases
        const existingBase = this.state.bases.find(b => String(b.variantId) === vid);
        variantQtys[vid] = existingBase ? (existingBase.quantity || 1) : 0;
      } else {
        // Charms use state.charms
        const existing = this.state.charms.find(c => c.cardEl === card && String(c.variantId) === vid);
        variantQtys[vid] = existing ? (existing.quantity || 1) : 0;
      }
    }

    // Default the current variant to 1 if nothing is selected yet
    // This way opening a modal and clicking "Add to Set" adds 1 of the visible variant
    const activeVid = String(currentVariant.id);
    if (variantQtys[activeVid] === 0) {
      variantQtys[activeVid] = 1;
    }

    this._charmModalState = {
      card,
      product,
      selectedVariantId: activeVid,
      variantQtys
    };

    // Populate modal
    const titleEl = overlay.querySelector('[data-charm-modal-title]');
    const imageEl = overlay.querySelector('[data-charm-modal-image]');
    const priceEl = overlay.querySelector('[data-charm-modal-price]');
    const ratingEl = overlay.querySelector('[data-charm-modal-rating]');
    const variantsWrap = overlay.querySelector('[data-charm-modal-variants]');
    const swatchesEl = overlay.querySelector('[data-charm-modal-swatches]');
    const qtyCount = overlay.querySelector('[data-charm-modal-qty-count]');

    if (titleEl) titleEl.textContent = product.title;
    if (qtyCount) qtyCount.textContent = String(variantQtys[activeVid]);

    // Image
    const variantImage = this.getVariantImage(product, currentVariant);
    if (imageEl) {
      imageEl.src = variantImage;
      imageEl.alt = product.title;
    }

    // Price
    if (priceEl) priceEl.textContent = this.formatMoney(Math.round(parseFloat(currentVariant.price) * 100));

    // Rating — copy from card
    if (ratingEl) {
      const cardRating = card.querySelector('.product-rating, .stamped-badge');
      ratingEl.innerHTML = cardRating ? cardRating.outerHTML : '';
    }

    // Description — from Shopify product admin (trusted content)
    const descEl = overlay.querySelector('[data-charm-modal-desc]');
    if (descEl) {
      if (product.body_html) {
        // Strip HTML tags for clean text display
        const tmp = document.createElement('div');
        tmp.innerHTML = product.body_html;
        descEl.textContent = tmp.textContent.trim();
        descEl.classList.remove('is-expanded');
      } else {
        descEl.textContent = '';
      }
    }

    // Variant swatches — only show if product has multiple available variants
    // BUT never show swatches for base (The Ends) cards — gold is gold, silver is silver
    const availableVariants = product.variants.filter(v => v.available !== false);
    if (swatchesEl && availableVariants.length > 1 && !isBaseCard) {
      variantsWrap.hidden = false;
      const swatch_config = this.getSwatchConfig();
      swatchesEl.innerHTML = availableVariants.map(v => {
        const colorName = v.title.split('/').pop().trim();
        const colorLower = colorName.toLowerCase();
        const swatchBg = swatch_config[colorLower] || colorLower;
        const isActive = v.id === currentVariant.id ? ' is-active' : '';
        const img = this.getVariantImage(product, v);
        const style = img
          ? `background-image: url(${this.escapeHtml(img)}); background-size: cover;`
          : `background: ${swatchBg};`;
        const vQty = variantQtys[String(v.id)] || 0;
        const badgeHtml = vQty > 0 ? `<span class="bundle-charm-modal__swatch-badge">${vQty}</span>` : `<span class="bundle-charm-modal__swatch-badge" hidden></span>`;
        return `<button type="button" class="bundle-charm-modal__swatch${isActive}" style="${style}" data-variant-id="${v.id}" title="${this.escapeHtml(v.title)}">${badgeHtml}<span class="sr-only">${this.escapeHtml(v.title)}</span></button>`;
      }).join('');
    } else if (variantsWrap) {
      variantsWrap.hidden = true;
    }

    // GA4 view_item — fire once per product per session
    const productId = String(product.id);
    if (!this._viewedProductIds.has(productId)) {
      this._viewedProductIds.add(productId);
      const priceDecimal = this.centsToDecimal(Math.round(parseFloat(currentVariant.price) * 100));
      this.pushDataLayer('view_item', {
        value: Number(priceDecimal),
        items: [{
          item_id: productId,
          item_name: product.title,
          item_brand: product.vendor || '',
          item_category: product.product_type || '',
          item_variant: currentVariant.title,
          price: Number(priceDecimal),
          quantity: 1
        }]
      });
    }

    // Show overlay
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';

    // Backdrop click to close
    this._charmModalOverlayHandler = (e) => { if (e.target === overlay) this.closeCharmModal(); };
    overlay.addEventListener('click', this._charmModalOverlayHandler);

    // Escape key
    this._charmModalEscHandler = (e) => { if (e.key === 'Escape') this.closeCharmModal(); };
    document.addEventListener('keydown', this._charmModalEscHandler);

    // Show/hide variant arrows (only for multi-variant non-base products)
    const prevArrow = overlay.querySelector('[data-action="charm-modal-prev-variant"]');
    const nextArrow = overlay.querySelector('[data-action="charm-modal-next-variant"]');
    const showArrows = availableVariants.length > 1 && !isBaseCard;
    if (prevArrow) prevArrow.hidden = !showArrows;
    if (nextArrow) nextArrow.hidden = !showArrows;
  }

  getVariantImage(product, variant) {
    if (variant.featured_image) return variant.featured_image.src || variant.featured_image;
    if (variant.image_id && product.images) {
      const img = product.images.find(i => i.id === variant.image_id);
      if (img) return img.src;
    }
    if (product.image) return product.image.src || product.image;
    if (product.images && product.images[0]) return product.images[0].src;
    return '';
  }

  getSwatchConfig() {
    if (this._swatchConfigCache) return this._swatchConfigCache;
    const config = {};
    const raw = document.querySelector('[data-swatch-config]');
    if (raw) {
      raw.textContent.split('\n').forEach(line => {
        const parts = line.split(':');
        if (parts.length >= 2) {
          config[parts[0].trim().toLowerCase()] = parts.slice(1).join(':').trim();
        }
      });
    }
    this._swatchConfigCache = config;
    return config;
  }

  selectCharmModalVariant(swatch) {
    const variantId = swatch.dataset.variantId;
    const state = this._charmModalState;
    if (!state) return;

    state.selectedVariantId = variantId;

    // Update active swatch
    const swatchesEl = this.querySelector('[data-charm-modal-swatches]');
    if (swatchesEl) {
      swatchesEl.querySelectorAll('.bundle-charm-modal__swatch').forEach(s => {
        s.classList.toggle('is-active', s.dataset.variantId === variantId);
      });
    }

    // Update image and price
    const variant = state.product.variants.find(v => String(v.id) === variantId);
    if (variant) {
      const imageEl = this.querySelector('[data-charm-modal-image]');
      const priceEl = this.querySelector('[data-charm-modal-price]');
      const img = this.getVariantImage(state.product, variant);
      if (imageEl && img) { imageEl.src = img; }
      if (priceEl) priceEl.textContent = this.formatMoney(Math.round(parseFloat(variant.price) * 100));
    }

    // Update qty display to show this variant's count
    const qtyCount = this.querySelector('[data-charm-modal-qty-count]');
    if (qtyCount) qtyCount.textContent = String(state.variantQtys[variantId] || 0);
  }

  /** Cycle through variants in the charm modal via arrow buttons */
  cycleCharmModalVariant(direction) {
    const state = this._charmModalState;
    if (!state) return;
    const availableVariants = state.product.variants.filter(v => v.available !== false);
    if (availableVariants.length <= 1) return;

    const currentIdx = availableVariants.findIndex(v => String(v.id) === state.selectedVariantId);
    const nextIdx = (currentIdx + direction + availableVariants.length) % availableVariants.length;
    const nextVariant = availableVariants[nextIdx];

    // Reuse selectCharmModalVariant by finding the matching swatch
    const swatch = this.querySelector(`.bundle-charm-modal__swatch[data-variant-id="${nextVariant.id}"]`);
    if (swatch) {
      this.selectCharmModalVariant(swatch);
    }
  }

  updateCharmModalQty(delta) {
    const state = this._charmModalState;
    if (!state) return;

    const vid = state.selectedVariantId;
    const current = state.variantQtys[vid] || 0;
    state.variantQtys[vid] = Math.max(0, current + delta);

    // Update qty display
    const countEl = this.querySelector('[data-charm-modal-qty-count]');
    if (countEl) countEl.textContent = String(state.variantQtys[vid]);

    // Update badge on the active swatch
    this.updateCharmModalSwatchBadge(vid);
  }

  updateCharmModalSwatchBadge(variantId) {
    const state = this._charmModalState;
    if (!state) return;
    const swatchesEl = this.querySelector('[data-charm-modal-swatches]');
    if (!swatchesEl) return;
    const swatch = swatchesEl.querySelector(`.bundle-charm-modal__swatch[data-variant-id="${variantId}"]`);
    if (!swatch) return;
    const badge = swatch.querySelector('.bundle-charm-modal__swatch-badge');
    if (!badge) return;
    const qty = state.variantQtys[variantId] || 0;
    if (qty > 0) {
      badge.textContent = String(qty);
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  }

  confirmCharmModal() {
    const state = this._charmModalState;
    if (!state) return;

    const { card, product, variantQtys } = state;
    const isBaseCard = card.classList.contains('bundle-charm-card--base') || card.dataset.isBase === 'true';

    if (isBaseCard) {
      // Route base cards to state.bases instead of state.charms
      for (const [vid, qty] of Object.entries(variantQtys)) {
        const variantId = parseInt(vid, 10);
        const existing = this.state.bases.find(b => b.variantId === variantId);

        if (qty > 0) {
          if (existing) {
            existing.quantity = qty;
          } else {
            // Add new base entry
            const variant = product.variants.find(v => String(v.id) === vid);
            if (!variant) continue;
            this.state.bases.push({
              variantId,
              productId: product.id,
              title: product.title,
              variantTitle: variant.title,
              price: Math.round(parseFloat(variant.price) * 100),
              image: this.getVariantImage(product, variant) || card.dataset.image,
              handle: product.handle,
              metal: variant.title?.toLowerCase()?.includes('gold') ? 'gold' : 'silver',
              quantity: qty
            });
          }
        } else if (existing) {
          this.state.bases = this.state.bases.filter(b => b.variantId !== variantId);
        }
      }

      // Update card UI
      const cardVariantId = parseInt(card.dataset.variantId, 10);
      const baseEntry = this.state.bases.find(b => b.variantId === cardVariantId);
      this.updateBaseGridCardUI(card, baseEntry ? baseEntry.quantity : 0);
      card.classList.toggle('is-selected', !!baseEntry);

      this.updateSummaryBar();
      this.updateProgressTracker();
      this.syncPicksStrip();
      this.closeCharmModal();
      return;
    }

    // Apply each variant's qty to state.charms
    for (const [vid, qty] of Object.entries(variantQtys)) {
      const variant = product.variants.find(v => String(v.id) === vid);
      if (!variant) continue;

      const existing = this.state.charms.find(c => String(c.variantId) === vid && c.cardEl === card);

      if (qty > 0) {
        if (existing) {
          existing.quantity = qty;
        } else {
          const img = this.getVariantImage(product, variant);
          this.state.charms.push({
            variantId: vid,
            productId: product.id,
            title: product.title,
            variantTitle: variant.title,
            price: Math.round(parseFloat(variant.price) * 100),
            image: img,
            handle: product.handle,
            cardEl: card,
            quantity: qty
          });
        }
      } else if (existing) {
        this.state.charms = this.state.charms.filter(c => c !== existing);
      }
    }

    // Determine if card should be selected (any variants with qty > 0)
    const anyLeft = this.state.charms.some(c => c.cardEl === card);
    card.classList.toggle('is-selected', anyLeft);

    // Sync the last-active variant back to the card display
    const activeVid = state.selectedVariantId;
    const activeVariant = product.variants.find(v => String(v.id) === activeVid);
    if (activeVariant) {
      const cardSwatches = card.querySelectorAll('.bundle-charm-variant');
      cardSwatches.forEach(s => {
        s.classList.toggle('is-active', s.dataset.variantId === activeVid);
      });
      card.dataset.variantId = activeVid;
      card.dataset.variantTitle = activeVariant.title;
      card.dataset.price = Math.round(parseFloat(activeVariant.price) * 100);
      const activeImg = this.getVariantImage(product, activeVariant);
      if (activeImg) this.swapCardImage(card, activeImg);
    }

    // Update card qty UI
    this.updateCardQuantityUI(card);
    this.updateSummaryBar();
    this.updateProgressTracker();
    this.syncPicksStrip();

    this.closeCharmModal();
  }

  closeCharmModal() {
    const overlay = this.querySelector('[data-charm-modal-overlay]');
    if (overlay) {
      overlay.hidden = true;
      if (this._charmModalOverlayHandler) {
        overlay.removeEventListener('click', this._charmModalOverlayHandler);
        this._charmModalOverlayHandler = null;
      }
    }
    document.body.style.overflow = '';
    if (this._charmModalEscHandler) {
      document.removeEventListener('keydown', this._charmModalEscHandler);
      this._charmModalEscHandler = null;
    }
    this._charmModalState = null;
  }

  updateGroupCardBadge(groupCard) {
    const badge = groupCard.querySelector('[data-group-badge]');
    const countEl = groupCard.querySelector('[data-group-badge-count]');
    if (!badge || !countEl) return;

    const count = this.state.charms.filter(c => c._groupCardEl === groupCard).length;
    if (count > 0) {
      badge.hidden = false;
      countEl.textContent = count;
      groupCard.classList.add('is-selected');
    } else {
      badge.hidden = true;
      groupCard.classList.remove('is-selected');
    }
  }

  /* ---------------------------------------------------------------
   * Deferred Charm Hydration -- moves <template data-deferred-charm>
   * contents into the grid on first step 2 entry to cut initial DOM size.
   * --------------------------------------------------------------- */
  hydrateDeferredCharms() {
    if (this._deferredHydrated) return;
    this._deferredHydrated = true;

    const grid = this.querySelector('[data-charms-grid]');
    if (!grid) return;

    const templates = grid.querySelectorAll('template[data-deferred-charm]');
    if (templates.length === 0) return;

    // Use a fragment to batch DOM insertions
    const fragment = document.createDocumentFragment();
    templates.forEach(tpl => {
      const content = tpl.content;
      while (content.firstChild) {
        fragment.appendChild(content.firstChild);
      }
    });
    grid.appendChild(fragment);

    // Remove the now-empty template elements
    templates.forEach(tpl => tpl.remove());
  }

  /**
   * Sort number charm cards so gold/silver pairs of the same style sit next to each other.
   * Extracts a style name from the product title, sorts by style then metal.
   * Only runs once.
   */
  sortNumberCards() {
    if (this._numbersSorted) return;
    this._numbersSorted = true;

    const grid = this.querySelector('[data-charms-grid]');
    if (!grid) return;

    const numberCards = Array.from(grid.querySelectorAll('.bundle-charm-card[data-categories~="numbers"]'));
    if (numberCards.length < 2) return;

    // Extract style from product title: remove common suffixes to get the style name
    const getStyle = (card) => {
      const title = (card.dataset.productTitle || '').toLowerCase();
      return title
        .replace(/number charms?/gi, '')
        .replace(/gold(en)?|silver|new/gi, '')
        .replace(/\s+/g, ' ')
        .trim() || 'plain';
    };

    const getMetal = (card) => {
      const title = (card.dataset.productTitle || '').toLowerCase();
      if (title.includes('gold')) return 'a_gold'; // a_ prefix so gold sorts before silver
      if (title.includes('silver')) return 'b_silver';
      return 'c_other';
    };

    numberCards.sort((a, b) => {
      const styleA = getStyle(a);
      const styleB = getStyle(b);
      if (styleA !== styleB) return styleA.localeCompare(styleB);
      return getMetal(a).localeCompare(getMetal(b));
    });

    // Find the first number card's position in the grid and re-insert sorted cards there
    const firstNumberCard = grid.querySelector('.bundle-charm-card[data-categories~="numbers"]');
    if (!firstNumberCard) return;

    // Insert all sorted number cards before the original first number card's next non-number sibling
    const anchor = firstNumberCard;
    for (const card of numberCards) {
      grid.insertBefore(card, anchor);
    }
  }

  /* ---------------------------------------------------------------
   * Step 1: Base Selection
   * --------------------------------------------------------------- */
  /** Toggle a base variant on/off. Supports multi-select with quantities. */
  toggleBase(card) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const existing = this.state.bases.find(b => b.variantId === variantId);

    if (existing) {
      // Remove this base
      this.state.bases = this.state.bases.filter(b => b.variantId !== variantId);
      card.classList.remove('is-selected');
      this.updateBaseCardQty(card, 0);
    } else {
      // Add this base with qty 1
      card.classList.add('is-selected');
      card.classList.remove('is-highlighted');
      this.state.bases.push({
        variantId,
        productId: parseInt(card.dataset.productId, 10),
        title: card.dataset.productTitle,
        variantTitle: card.dataset.variantTitle,
        price: parseInt(card.dataset.variantPrice, 10),
        image: card.dataset.variantImage,
        metal: card.dataset.metal,
        quantity: 1
      });
      this.updateBaseCardQty(card, 1);
    }

    this.updateProgressTracker();
    this.updateSummaryBar();

    // If quiz was waiting for base selection ("Mix Both" flow), continue now
    if (this.pendingQuizResults) {
      const results = this.pendingQuizResults;
      this.pendingQuizResults = null;
      const selectedMetal = card.dataset.metal || 'gold';
      this.state.quizFilters = { ...results, metal: selectedMetal };
      setTimeout(() => this.continueQuizFlow(results, selectedMetal), 300);
    }
  }

  /** Add a base from toggleBase or auto-select (used by quiz path) */
  selectBase(card) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const existing = this.state.bases.find(b => b.variantId === variantId);
    if (!existing) {
      this.toggleBase(card);
    }
  }

  /** Increase quantity of a base variant */
  increaseBase(card) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const base = this.state.bases.find(b => b.variantId === variantId);
    if (base) {
      base.quantity++;
      this.updateBaseCardQty(card, base.quantity);
      this.updateProgressTracker();
      this.updateSummaryBar();
    }
  }

  /** Decrease quantity of a base variant */
  decreaseBase(card) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const base = this.state.bases.find(b => b.variantId === variantId);
    if (base) {
      base.quantity--;
      if (base.quantity <= 0) {
        this.state.bases = this.state.bases.filter(b => b.variantId !== variantId);
        card.classList.remove('is-selected');
        this.updateBaseCardQty(card, 0);
      } else {
        this.updateBaseCardQty(card, base.quantity);
      }
      this.updateProgressTracker();
      this.updateSummaryBar();
    }
  }

  /** Update the quantity display on a base card */
  updateBaseCardQty(card, qty) {
    const stepper = card.querySelector('[data-base-stepper]');
    const addBtn = card.querySelector('[data-action="add-base"]');
    const countEl = card.querySelector('[data-base-qty-count]');

    if (qty > 0) {
      if (stepper) stepper.hidden = false;
      if (addBtn) addBtn.hidden = true;
      if (countEl) countEl.textContent = qty;
    } else {
      if (stepper) stepper.hidden = true;
      if (addBtn) addBtn.hidden = false;
    }
  }

  /** Add The Ends from the charms grid to bases */
  addBaseFromGrid(card) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const existing = this.state.bases.find(b => b.variantId === variantId);
    if (existing) {
      existing.quantity++;
    } else {
      this.state.bases.push({
        variantId,
        productId: parseInt(card.dataset.productId, 10),
        title: card.dataset.productTitle,
        variantTitle: card.dataset.variantTitle,
        price: parseInt(card.dataset.price, 10),
        image: card.dataset.image,
        metal: card.dataset.variantTitle?.toLowerCase()?.includes('gold') ? 'gold' : 'silver',
        quantity: 1
      });
    }
    // Visual-only update — do NOT add to state.charms (bases have their own state)
    card.classList.add('is-selected');
    const baseQty = this.state.bases.find(b => b.variantId === variantId)?.quantity || 0;
    this.updateBaseGridCardUI(card, baseQty);
    this.syncStep1BaseCards();
    this.updateProgressTracker();
    this.updateSummaryBar();
    this.syncPicksStrip();
  }

  /** Remove The Ends from the charms grid */
  removeBaseFromGrid(card) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const base = this.state.bases.find(b => b.variantId === variantId);
    if (base) {
      base.quantity--;
      if (base.quantity <= 0) {
        this.state.bases = this.state.bases.filter(b => b.variantId !== variantId);
      }
    }
    // Visual-only update — do NOT remove from state.charms
    const remaining = this.state.bases.find(b => b.variantId === variantId);
    if (!remaining) card.classList.remove('is-selected');
    this.updateBaseGridCardUI(card, remaining ? remaining.quantity : 0);
    this.syncStep1BaseCards();
    this.updateProgressTracker();
    this.updateSummaryBar();
    this.syncPicksStrip();
  }

  /** Update the visual qty display on a base card in the grid (no state mutation) */
  updateBaseGridCardUI(card, qty) {
    const stepper = card.querySelector('[data-card-stepper]');
    const addBtn = card.querySelector('.bundle-card-qty__add');
    const countEl = card.querySelector('[data-card-qty-count]');
    if (qty > 0) {
      if (stepper) stepper.hidden = false;
      if (addBtn) addBtn.hidden = true;
      if (countEl) countEl.textContent = qty;
    } else {
      if (stepper) stepper.hidden = true;
      if (addBtn) addBtn.hidden = false;
    }
  }

  /** Sync state.bases quantities to The Ends cards in the Step 2 base section */
  syncBaseCardsInGrid() {
    const baseSection = this.querySelector('[data-base-section]');
    if (!baseSection) return;

    baseSection.querySelectorAll('.bundle-charm-card--base').forEach(card => {
      const variantId = parseInt(card.dataset.variantId, 10);
      const base = this.state.bases.find(b => b.variantId === variantId);
      const qty = base ? base.quantity : 0;

      if (qty > 0) {
        card.classList.add('is-selected');
        // Update the stepper UI
        const stepper = card.querySelector('[data-card-stepper]');
        const addBtn = card.querySelector('.bundle-card-qty__add');
        const countEl = card.querySelector('[data-card-qty-count]');
        if (stepper) stepper.hidden = false;
        if (addBtn) addBtn.hidden = true;
        if (countEl) countEl.textContent = qty;
      } else {
        card.classList.remove('is-selected');
        const stepper = card.querySelector('[data-card-stepper]');
        const addBtn = card.querySelector('.bundle-card-qty__add');
        if (stepper) stepper.hidden = true;
        if (addBtn) addBtn.hidden = false;
      }
    });
  }

  /** Sync Step 1 base card UI to match current state.bases (called when bases change from Step 2 or review) */
  syncStep1BaseCards() {
    this.querySelectorAll('.bundle-base-card').forEach(card => {
      const variantId = parseInt(card.dataset.variantId, 10);
      const base = this.state.bases.find(b => b.variantId === variantId);
      const qty = base ? base.quantity : 0;
      if (qty > 0) {
        card.classList.add('is-selected');
      } else {
        card.classList.remove('is-selected');
      }
      this.updateBaseCardQty(card, qty);
    });
  }

  /* ---------------------------------------------------------------
   * Step 2: Charm Filtering
   * --------------------------------------------------------------- */
  setFilter(filter) {
    this.state.filter = filter;
    this.state.subFilter = null;
    this.state.styleFilter = null;

    // Update active tab and detect special filter types from data attributes
    let isLetterFilter = false;
    let isNumberFilter = false;
    this.querySelectorAll('.bundle-filter-btn').forEach(btn => {
      const isActive = btn.dataset.filter === filter;
      btn.classList.toggle('is-active', isActive);
      if (isActive) {
        isLetterFilter = btn.dataset.isLetters === 'true';
        isNumberFilter = btn.dataset.isNumbers === 'true';
      }
    });

    // Show/hide style filter (for letters and numbers)
    const styleWrap = this.querySelector('[data-style-filter]');
    if (isLetterFilter || isNumberFilter) {
      this.buildStyleFilter(filter);
    } else if (styleWrap) {
      styleWrap.hidden = true;
    }

    // Show/hide letter sub-filter (only if there are per-variant letter cards)
    const letterFilter = this.querySelector('[data-letter-filter]');
    const hasPerVariantLetters = this.querySelector(`.bundle-charm-card[data-per-variant][data-categories~="${filter}"]`);
    if (letterFilter) letterFilter.hidden = !isLetterFilter || !hasPerVariantLetters;

    // Show/hide number sub-filter (only if there are per-variant number cards)
    const numberFilter = this.querySelector('[data-number-filter]');
    const hasPerVariantNumbers = this.querySelector(`.bundle-charm-card[data-per-variant][data-categories~="${filter}"]`);
    if (numberFilter) numberFilter.hidden = !isNumberFilter || !hasPerVariantNumbers;

    // Reset sub-filter active states
    this.querySelectorAll('[data-letter-filter-val], [data-number-filter-val]').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.letterFilterVal === 'all' || btn.dataset.numberFilterVal === 'all');
    });

    // Apply all filters
    this.applyFilters(filter, this.state.styleFilter, null);
  }

  /** Apply category filter + optional style filter + optional sub-filter (letter/number) */
  applyFilters(filter, styleFilter, subFilter) {
    this.querySelectorAll('.bundle-charm-card').forEach(card => {
      // Never hide base cards — they have their own section
      if (card.dataset.isBase) {
        card.hidden = false;
        return;
      }

      if (filter === 'all') {
        card.hidden = false;
        return;
      }

      const categories = (card.dataset.categories || '').split(' ');
      let show = categories.includes(filter);

      // Style filter: match data-style attribute (only for per-variant cards, not group cards)
      if (show && styleFilter && card.dataset.style && card.dataset.perVariant) {
        show = card.dataset.style === styleFilter;
      }

      // Sub-filter: match variant label against letter or number (only for per-variant cards)
      if (show && subFilter && card.dataset.perVariant) {
        const label = (card.dataset.variantLabel || '').toLowerCase();
        show = label === subFilter.toLowerCase();
      }

      card.hidden = !show;
    });
  }

  /** Handle letter/number sub-filter clicks */
  handleSubFilter(val, type) {
    const subFilter = val === 'all' ? null : val;
    this.state.subFilter = subFilter;

    // Update active button
    const selector = type === 'letter' ? '[data-letter-filter-val]' : '[data-number-filter-val]';
    this.querySelectorAll(selector).forEach(btn => {
      const btnVal = btn.dataset.letterFilterVal || btn.dataset.numberFilterVal;
      btn.classList.toggle('is-active', btnVal === val);
    });

    this.applyFilters(this.state.filter, this.state.styleFilter, subFilter);
  }

  /** Handle style sub-filter clicks */
  handleStyleFilter(val) {
    this.state.styleFilter = val === 'all' ? null : val;

    this.querySelectorAll('[data-style-filter-val]').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.styleFilterVal === val);
    });

    this.applyFilters(this.state.filter, this.state.styleFilter, this.state.subFilter);
  }

  /**
   * Build the style filter buttons dynamically from cards in the current category.
   * Reads data-style attributes from per-variant cards and creates a button per unique style.
   */
  buildStyleFilter(category) {
    const styleRow = this.querySelector('[data-style-filter-row]');
    const styleWrap = this.querySelector('[data-style-filter]');
    if (!styleRow || !styleWrap) return;

    // Collect unique styles from visible per-variant cards in this category
    const styles = new Map(); // handle → display name
    this.querySelectorAll(`.bundle-charm-card[data-per-variant][data-style]`).forEach(card => {
      const cats = (card.dataset.categories || '').split(' ');
      if (cats.includes(category)) {
        const styleHandle = card.dataset.style;
        if (styleHandle && !styles.has(styleHandle)) {
          // Convert handle back to display name: "pink-gold" → "Pink Gold"
          const name = styleHandle.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          styles.set(styleHandle, name);
        }
      }
    });

    if (styles.size <= 1) {
      styleWrap.hidden = true;
      return;
    }

    // Build buttons
    let html = '<button type="button" class="bundle-style-filter__btn" data-style-filter-val="all">All styles</button>';
    let first = true;
    for (const [handle, name] of styles) {
      html += `<button type="button" class="bundle-style-filter__btn${first ? ' is-active' : ''}" data-style-filter-val="${handle}">${name}</button>`;
      first = false;
    }

    styleRow.innerHTML = html;
    styleWrap.hidden = false;

    // Bind clicks
    styleRow.querySelectorAll('[data-style-filter-val]').forEach(btn => {
      btn.addEventListener('click', () => this.handleStyleFilter(btn.dataset.styleFilterVal));
    });

    // Auto-select first style (don't show 364 cards at once)
    const firstStyle = [...styles.keys()][0];
    this.state.styleFilter = firstStyle;
  }

  /* ---------------------------------------------------------------
   * Step 2: Charm Toggle
   * --------------------------------------------------------------- */
  /** Add one or increment quantity of a charm via its grid card.
   *  Variant-aware: if the active swatch differs from an existing entry, a new entry is created. */
  increaseCharmOnCard(card) {
    const activeVariant = card.querySelector('.bundle-charm-variant.is-active');
    const activeVariantId = parseInt(activeVariant?.dataset.variantId || card.dataset.variantId, 10);

    // Look for an existing entry with the SAME variant (not just the same card)
    const existing = this.state.charms.find(c => c.cardEl === card && c.variantId === activeVariantId);

    if (existing) {
      // Same card + same variant — bump quantity
      existing.quantity = (existing.quantity || 1) + 1;
    } else {
      // New variant (or first add) — create a new entry
      card.classList.add('is-selected');

      this.state.charms.push({
        variantId: activeVariantId,
        productId: parseInt(card.dataset.productId, 10),
        title: card.dataset.productTitle,
        variantTitle: activeVariant?.dataset.variantTitle || card.dataset.variantTitle || '',
        price: parseInt(activeVariant?.dataset.price || card.dataset.price, 10),
        image: activeVariant?.dataset.image || card.dataset.image,
        handle: card.dataset.handle,
        cardEl: card,
        quantity: 1
      });

      this.pushDataLayer('select_item', {
        items: [{
          item_id: String(card.dataset.productId),
          item_name: card.dataset.productTitle,
          item_variant: activeVariant?.dataset.variantTitle || '',
          price: this.centsToDecimal(activeVariant?.dataset.price || card.dataset.price)
        }]
      });

    }

    this.syncPicksStrip();
    this.updateCardQuantityUI(card);
    this.updateSummaryBar();
  }

  /** Decrease quantity on a charm card; variant-aware — decreases the currently active variant.
   *  Only deselects the card if no variants remain. */
  decreaseCharmOnCard(card) {
    const activeVariant = card.querySelector('.bundle-charm-variant.is-active');
    const activeVariantId = parseInt(activeVariant?.dataset.variantId || card.dataset.variantId, 10);

    // Find the entry for the active variant
    let charm = this.state.charms.find(c => c.cardEl === card && c.variantId === activeVariantId);

    // If no exact match (e.g. user switched swatch after adding), decrease the last entry for this card
    if (!charm) {
      const cardCharms = this.state.charms.filter(c => c.cardEl === card);
      if (cardCharms.length === 0) return;
      charm = cardCharms[cardCharms.length - 1];
    }

    // GA4 remove_from_cart for the decreased item
    this.pushDataLayer('remove_from_cart', {
      value: this.centsToDecimal(charm.price || 0),
      items: [{
        item_id: String(charm.productId),
        item_name: charm.title,
        item_variant: charm.variantTitle || '',
        price: this.centsToDecimal(charm.price || 0),
        quantity: 1
      }]
    });

    charm.quantity = (charm.quantity || 1) - 1;
    if (charm.quantity <= 0) {
      this.state.charms = this.state.charms.filter(c => c !== charm);
      // Only deselect card if no variants remain for this card
      const anyLeft = this.state.charms.some(c => c.cardEl === card);
      if (!anyLeft) card.classList.remove('is-selected');
    }

    this.syncPicksStrip();
    this.updateCardQuantityUI(card);
    this.updateSummaryBar();
  }

  /** Update the quantity badge on a card to reflect current state.
   *  Shows total quantity across all variant entries for this card. */
  updateCardQuantityUI(card) {
    const totalQty = this.state.charms
      .filter(c => c.cardEl === card)
      .reduce((sum, c) => sum + (c.quantity || 1), 0);

    const countEl = card.querySelector('[data-card-qty-count]');
    const stepper = card.querySelector('[data-card-stepper]');
    const addBtn = card.querySelector('.bundle-card-qty__add');

    if (totalQty > 0) {
      if (countEl) countEl.textContent = totalQty;
      if (stepper) stepper.hidden = false;
      if (addBtn) addBtn.hidden = true;
    } else {
      if (stepper) stepper.hidden = true;
      if (addBtn) addBtn.hidden = false;
    }
  }

  /** Legacy alias — some code paths still call toggleCharm */
  toggleCharm(card) {
    if (card.classList.contains('is-selected')) {
      this.decreaseCharmOnCard(card);
    } else {
      this.increaseCharmOnCard(card);
    }
  }

  selectCharmVariant(btn) {
    const card = btn.closest('.bundle-charm-card');
    if (!card) return;

    // Update active variant
    card.querySelectorAll('.bundle-charm-variant').forEach(v => v.classList.remove('is-active'));
    btn.classList.add('is-active');

    // Update card data attributes
    if (btn.dataset.variantId) card.dataset.variantId = btn.dataset.variantId;
    if (btn.dataset.price) card.dataset.price = btn.dataset.price;

    // Update displayed image (must clear srcset or browser ignores src change)
    if (btn.dataset.image) {
      this.swapCardImage(card, btn.dataset.image);
    } else {
      // No image on swatch — fetch from product API as fallback
      this.fetchVariantImage(card.dataset.handle, btn.dataset.variantId).then(imageUrl => {
        if (imageUrl) {
          btn.dataset.image = imageUrl; // Cache on the swatch for future clicks
          this.swapCardImage(card, imageUrl);
        }
      });
    }

    // Update price display — try both price-list and legacy price element
    const priceEl = card.querySelector('.bundle-charm-card__price') || card.querySelector('sale-price');
    if (priceEl && btn.dataset.price) {
      priceEl.textContent = this.formatMoney(parseInt(btn.dataset.price, 10));
    }

    // Do NOT update state.charms here — swatch changes only affect the card's visual state.
    // Next time the user clicks "+", increaseCharmOnCard() will add the new variant as a separate entry.
  }

  /**
   * Fetch variant image from product API when swatch doesn't have data-image.
   * Uses a per-handle cache to avoid re-fetching.
   */
  async fetchVariantImage(handle, variantId) {
    if (!handle || !variantId) return null;

    try {
      let product = this._productCache.get(handle);
      if (!product) {
        product = await this.fetchProduct(handle);
        if (product) this._productCache.set(handle, product);
      }
      if (!product) return null;

      const vid = parseInt(variantId, 10);
      const variant = product.variants.find(v => v.id === vid);
      if (!variant) return null;

      // Check variant's image_id against product images
      if (variant.image_id && product.images) {
        const img = product.images.find(i => i.id === variant.image_id);
        if (img) return img.src;
      }
      if (variant.featured_image) return variant.featured_image.src;

      return null;
    } catch (e) {
      console.warn('fetchVariantImage failed:', handle, e);
      return null;
    }
  }

  /* ---------------------------------------------------------------
   * Summary Bar (Step 2 sticky bar)
   * --------------------------------------------------------------- */
  updateSummaryBar() {
    const totalItems = this.getTotalItemCount();
    const baseCount = this.state.bases.reduce((sum, b) => sum + b.quantity, 0);
    const charmCount = totalItems - baseCount;
    const tier = this.getCurrentTier();
    const subtotal = this.getSubtotal();
    const discountAmount = tier ? Math.round(subtotal * tier.discount / 100) : 0;
    const total = subtotal - discountAmount;

    const countEl = this.getRootEl().querySelector('[data-charm-count]');
    if (countEl) countEl.textContent = `${totalItems} item${totalItems !== 1 ? 's' : ''}`;

    const discountEl = this.getRootEl().querySelector('[data-discount-label]');
    if (discountEl) {
      // Only show discount badge when a tier is active; "Add X more" is shown in the progress bar message
      discountEl.textContent = tier ? `${tier.discount}% off` : '';
    }

    const totalEl = this.getRootEl().querySelector('[data-running-total]');
    if (totalEl) {
      totalEl.textContent = this.formatMoney(discountAmount > 0 ? total : subtotal);
    }

    const footerAtcBtn = this.getRootEl().querySelector('[data-footer-atc-btn]');
    if (footerAtcBtn) {
      footerAtcBtn.textContent = 'Review your bundle';
      footerAtcBtn.disabled = this.getTotalItemCount() === 0;
    }

    this.updateProgressTracker();
  }

  /* ---------------------------------------------------------------
   * Progress Tracker (visual tier bar)
   * --------------------------------------------------------------- */
  updateProgressTracker() {
    const tracker = this.getRootEl().querySelector('[data-progress-tracker]');
    if (!tracker) return;

    const fill = tracker.querySelector('[data-progress-fill]');
    const msg = tracker.querySelector('[data-progress-msg]');
    const milestones = tracker.querySelectorAll('[data-milestone]');
    const count = this.getTotalItemCount();
    const maxTier = this.tiers[this.tiers.length - 1];
    if (!maxTier) return;

    // Calculate fill percentage — interpolate between evenly-spaced milestone positions
    // Milestones are at 0%, 25%, 50%, 75%, 100% matching tier boundaries
    const segments = [0, ...this.tiers.map(t => t.min)]; // [0, 5, 8, 10, 12]
    const positions = segments.map((_, i) => (i / (segments.length - 1)) * 100); // [0, 25, 50, 75, 100]
    let fillPct = 0;
    if (count >= segments[segments.length - 1]) {
      fillPct = 100;
    } else {
      for (let i = 1; i < segments.length; i++) {
        if (count < segments[i]) {
          const segStart = segments[i - 1];
          const segEnd = segments[i];
          const posStart = positions[i - 1];
          const posEnd = positions[i];
          const progress = (count - segStart) / (segEnd - segStart);
          fillPct = posStart + progress * (posEnd - posStart);
          break;
        }
      }
    }
    if (fill) fill.style.width = `${fillPct}%`;

    // Update milestone states
    const currentTier = this.getCurrentTier();
    const nextTier = this.getNextTier();

    milestones.forEach(m => {
      const idx = parseInt(m.dataset.milestone, 10);
      m.classList.remove('is-reached', 'is-active');

      if (idx === 0) {
        // Start dot is always reached if we have any items
        if (count > 0) m.classList.add('is-reached');
        return;
      }

      const tier = this.tiers[idx - 1];
      if (!tier) return;

      if (currentTier && tier.discount <= currentTier.discount) {
        m.classList.add('is-reached');
      }
      if (currentTier && tier.discount === currentTier.discount) {
        m.classList.add('is-active');
      }
    });

    // Update message text
    if (msg) {
      if (currentTier) {
        if (nextTier) {
          const remaining = nextTier.min - count;
          msg.innerHTML = `<span class="accent">${currentTier.discount}% off unlocked!</span> Add ${remaining} more for ${nextTier.discount}%`;
        } else {
          msg.innerHTML = `<span class="accent">${currentTier.discount}% off</span> &mdash; max discount reached!`;
        }
      } else if (nextTier) {
        const remaining = nextTier.min - count;
        msg.innerHTML = `Add <span class="accent">${remaining} more item${remaining !== 1 ? 's' : ''}</span> to unlock ${nextTier.discount}% off`;
      } else {
        msg.textContent = '';
      }
    }
  }

  /* ---------------------------------------------------------------
   * Step Navigation
   * --------------------------------------------------------------- */
  goToStep(step) {
    if (step !== 1) return;

    this.state.step = step;

    // Ensure the single panel is visible
    this.querySelectorAll('.bundle-builder__panel').forEach(panel => {
      panel.classList.add('is-active');
    });

    // Always hydrate/sync on the single builder step
    this.hydrateDeferredCharms();
    this.sortNumberCards();
    this.syncBaseCardsInGrid();
    this.syncPicksStrip();

    this.pushDataLayer('view_item_list', {
      item_list_id: 'bundle_builder_charms',
      item_list_name: 'Bundle Builder - Charms',
      items: Array.from(this.querySelectorAll('.bundle-charm-card:not([hidden])')).slice(0, 20).map((card, idx) => ({
        item_id: card.dataset.productId,
        item_name: card.dataset.productTitle,
        price: this.centsToDecimal(card.dataset.price),
        index: idx
      }))
    });
    this.updateSummaryBar();

    // Always update progress tracker (it's a fixed footer, visible on all steps)
    this.updateProgressTracker();

    // Update footer buttons
    const footerQuizBtn = this.getRootEl().querySelector('[data-footer-quiz-btn]');
    const footerAtcBtn = this.getRootEl().querySelector('[data-footer-atc-btn]');

    // Always hide quiz button in builder mode
    if (footerQuizBtn) footerQuizBtn.hidden = true;

    if (footerAtcBtn) {
      footerAtcBtn.hidden = false;
      footerAtcBtn.textContent = 'Review your bundle';
      footerAtcBtn.disabled = this.getTotalItemCount() === 0;
    }

    this.scrollPageToBuilderTop();
  }

  scrollPageToBuilderTop() {
    const root = this.getRootEl();
    const top = window.scrollY + root.getBoundingClientRect().top - 16;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  /* ---------------------------------------------------------------
   * Step 3: Review
   * --------------------------------------------------------------- */
  renderReviewItem(item, idx) {
    return `
      <div class="bundle-review-item">
        <img class="bundle-review-item__image" src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" width="56" height="56" loading="lazy">
        <div>
          <div class="bundle-review-item__title">${this.escapeHtml(item.title)}</div>
          ${item.variant ? `<div class="text-xs text-subdued">${this.escapeHtml(item.variant)}</div>` : ''}
        </div>
        <div class="bundle-review-item__price">${this.formatMoney(item.price * (item.quantity || 1))}</div>
        <div class="bundle-review-qty">
          <button type="button" class="bundle-review-qty__btn" data-qty-action="decrease" data-review-idx="${idx}" data-is-base="${item.isBase}" aria-label="Decrease quantity">&minus;</button>
          <span class="bundle-review-qty__count">${item.quantity || 1}</span>
          <button type="button" class="bundle-review-qty__btn" data-qty-action="increase" data-review-idx="${idx}" data-is-base="${item.isBase}" aria-label="Increase quantity">&plus;</button>
        </div>
      </div>`;
  }

  renderReview() {
    const listEl = this.querySelector('[data-review-list]');
    const totalsEl = this.querySelector('[data-review-totals]');

    if (!listEl || !totalsEl) return;

    const allItems = [];
    const baseCount = this.state.bases.length;

    // Base items (with quantity steppers)
    this.state.bases.forEach(base => {
      allItems.push({
        title: base.title,
        variant: base.variantTitle,
        price: base.price,
        image: base.image,
        isBase: true,
        quantity: base.quantity,
        variantId: base.variantId
      });
    });

    // Charm items
    this.state.charms.forEach(charm => {
      allItems.push({
        title: charm.title,
        variant: charm.variantTitle,
        price: charm.price,
        image: charm.image,
        isBase: false,
        cardEl: charm.cardEl,
        _groupCardEl: charm._groupCardEl,
        quantity: charm.quantity || 1
      });
    });

    // Render list with section headers — all data from internal state, escaped
    let listHtml = '';

    // Base section
    if (baseCount > 0) {
      listHtml += `<div class="bundle-review-section-label">Base</div>`;
      allItems.filter(i => i.isBase).forEach((item, idx) => {
        listHtml += this.renderReviewItem(item, idx);
      });
    }

    // Charms section
    const charmItems = allItems.filter(i => !i.isBase);
    if (charmItems.length > 0) {
      listHtml += `<div class="bundle-review-section-label">Charms (${charmItems.reduce((s, c) => s + (c.quantity || 1), 0)})</div>`;
      charmItems.forEach((item, idx) => {
        listHtml += this.renderReviewItem(item, baseCount + idx);
      });
    }

    listEl.innerHTML = listHtml;

    // Bind quantity stepper buttons
    listEl.querySelectorAll('[data-qty-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.reviewIdx, 10);
        const action = btn.dataset.qtyAction;
        const isBase = btn.dataset.isBase === 'true';

        if (isBase) {
          const base = this.state.bases[idx];
          if (!base) return;
          if (action === 'increase') {
            base.quantity++;
          } else if (action === 'decrease') {
            base.quantity--;
            if (base.quantity <= 0) {
              this.state.bases.splice(idx, 1);
            }
          }
          this.syncStep1BaseCards();
          this.syncBaseCardsInGrid();
          this.renderReview();
          this.updateSummaryBar();
          return;
        }

        const charmIdx = idx - baseCount;
        const charm = this.state.charms[charmIdx];
        if (!charm) return;

        if (action === 'increase') {
          charm.quantity = (charm.quantity || 1) + 1;
          if (charm.cardEl) this.updateCardQuantityUI(charm.cardEl);
          this.renderReview();
        } else if (action === 'decrease') {
          charm.quantity = (charm.quantity || 1) - 1;
          if (charm.quantity <= 0) {
            const cardEl = charm.cardEl;
            const groupCard = charm._groupCardEl;
            this.state.charms.splice(charmIdx, 1);
            if (cardEl) {
              const anyLeft = this.state.charms.some(c => c.cardEl === cardEl);
              if (!anyLeft) cardEl.classList.remove('is-selected');
              this.updateCardQuantityUI(cardEl);
            }
            if (groupCard) this.updateGroupCardBadge(groupCard);
            this.syncPicksStrip();
          } else if (charm.cardEl) {
            this.updateCardQuantityUI(charm.cardEl);
          }
          this.renderReview();
        }
        this.updateSummaryBar();
      });
    });

    // Totals
    const subtotal = this.getSubtotal();
    const tier = this.getCurrentTier();
    const discountAmount = tier ? Math.round(subtotal * tier.discount / 100) : 0;
    const total = subtotal - discountAmount;

    // Compact totals
    let totalsHtml = '';

    if (tier) {
      totalsHtml += `
        <div class="bundle-review-totals__row is-total">
          <span>Total <span class="bundle-review-totals__savings">(${tier.discount}% off &mdash; you save ${this.formatMoney(discountAmount)})</span></span>
          <span>${this.formatMoney(total)}</span>
        </div>
      `;
    } else {
      const nextTier = this.getNextTier();
      totalsHtml += `
        <div class="bundle-review-totals__row is-total">
          <span>Total</span>
          <span>${this.formatMoney(subtotal)}</span>
        </div>
      `;
      if (nextTier) {
        const remaining = nextTier.min - this.getTotalItemCount();
        totalsHtml += `
          <div class="bundle-review-totals__upsell">Add ${remaining} more item${remaining !== 1 ? 's' : ''} for ${nextTier.discount}% off</div>
        `;
      }
    }

    totalsEl.innerHTML = totalsHtml;

    // Update footer ATC button
    const footerAtcBtn = this.getRootEl().querySelector('[data-footer-atc-btn]');
    if (footerAtcBtn) {
      footerAtcBtn.disabled = this.getTotalItemCount() === 0;
      footerAtcBtn.textContent = 'Review your bundle';
    }
  }

  /* ---------------------------------------------------------------
   * Add to Cart + Discount Code Application
   * --------------------------------------------------------------- */
  /** Open the cart drawer (or redirect to /cart) */
  openCartDrawer() {
    if (this.cartType === 'drawer') {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else {
        window.location.href = '/cart';
      }
    } else {
      window.location.href = '/cart';
    }
  }

  async addToCart() {
    if (this.state.loading) return;
    if (this.state.charms.length === 0 && this.state.bases.length === 0) return;

    this.state.loading = true;
    const atcBtn = this.getRootEl().querySelector('[data-footer-atc-btn]');
    if (atcBtn) {
      atcBtn.classList.add('is-loading');
      atcBtn.dataset.prevText = atcBtn.textContent;
      atcBtn.textContent = 'Adding...';
    }

    const bundleId = 'bundle-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
    const tier = this.getCurrentTier();
    const discountLabel = tier ? `${tier.discount}% Bundle Discount` : '';

    const items = [];

    // Add bases
    for (const base of this.state.bases) {
      items.push({
        id: base.variantId,
        quantity: base.quantity,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_role': 'base',
          ...(discountLabel ? { '_bundle_discount': discountLabel } : {})
        }
      });
    }

    // Add charms
    this.state.charms.forEach(charm => {
      items.push({
        id: charm.variantId,
        quantity: charm.quantity || 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_role': 'charm',
          ...(discountLabel ? { '_bundle_discount': discountLabel } : {})
        }
      });
    });

    try {
      // 1. Clear any previous bundle items from cart
      try {
        const prevCart = await fetch('/cart.js');
        const prevCartData = await prevCart.json();
        const updates = {};
        for (const item of prevCartData.items) {
          if (item.properties && item.properties._bundle_id) {
            updates[item.key] = 0;
          }
        }
        if (Object.keys(updates).length > 0) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
        }
      } catch (e) { /* continue even if cleanup fails */ }

      // 2. Add current bundle items
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Bundle-Builder': '1' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Cart add failed');
      await response.json();

      // 3. DataLayer tracking (sync — fast, no network)
      const allItems = [...this.state.bases, ...this.state.charms];
      const subtotal = this.getSubtotal();
      const discountAmount = tier ? Math.round(subtotal * tier.discount / 100) : 0;

      this.pushDataLayer('add_to_cart', {
        value: this.centsToDecimal(subtotal - discountAmount),
        items: allItems.map(item => ({
          item_id: String(item.productId),
          item_name: item.title,
          item_variant: item.variantTitle || '',
          price: this.centsToDecimal(item.price),
          quantity: item.quantity || 1
        }))
      });

      // 4. Klaviyo tracking — fire-and-forget (no await)
      if (window._learnq) {
        (async () => {
          try {
            const cartRes = await fetch('/cart.js');
            const cartData = await cartRes.json();
            window._learnq.push(['track', 'Added to Cart', {
              total_price: cartData.total_price / 100,
              $value: cartData.total_price / 100,
              items: cartData.items,
              _bundle: true
            }]);
          } catch (e) { /* silent */ }
        })();
      }

      // 5. Apply discount code — fire-and-forget
      if (tier) {
        const code = `${this.discountPrefix}${tier.discount}`;
        document.cookie = `discount_code=${code}; path=/; max-age=3600; SameSite=Lax`;
        this.pushCustomEvent('discount_code_applied', { code, discount: tier.discount });
        this.applyDiscountViaIframe(code);
      }

      // 6. Open cart drawer immediately
      this.openCartDrawer();

      // 7. Inject savings display into cart drawer (after theme re-renders)
      if (tier) {
        const savings = Math.round(subtotal * tier.discount / 100);
        this.injectCartSavingsAfterRefresh(savings, tier.discount);
      }

    } catch (err) {
      console.error('Bundle add to cart failed:', err);
      if (atcBtn) atcBtn.textContent = 'Something went wrong. Try again.';
      setTimeout(() => this.updateSummaryBar(), 2000);
    } finally {
      this.state.loading = false;
      if (atcBtn) {
        atcBtn.classList.remove('is-loading');
        atcBtn.textContent = atcBtn.dataset.prevText || 'Review your bundle';
      }
    }
  }

  /** Wait for the Prestige theme to finish re-rendering the cart drawer, then inject savings */
  injectCartSavingsAfterRefresh(savingsCents, discountPct) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) {
      // No drawer — inject immediately (fallback for /cart page)
      setTimeout(() => this.injectCartSavings(savingsCents, discountPct), 500);
      return;
    }

    let injected = false;
    const doInject = () => {
      if (injected) return;
      injected = true;
      observer.disconnect();
      // Small delay after mutation to let the theme finish its render cycle
      setTimeout(() => this.injectCartSavings(savingsCents, discountPct), 150);
    };

    const observer = new MutationObserver(doInject);
    observer.observe(drawer, { childList: true, subtree: true });

    // Safety: if no mutation fires within 3s, inject anyway
    setTimeout(doInject, 3000);
  }

  /** Inject a savings line into the Shopify cart drawer (Prestige theme) */
  injectCartSavings(savingsCents, discountPct) {
    // Remove any previous savings line
    document.querySelectorAll('.bundle-cart-savings').forEach(el => el.remove());

    // Prestige theme: .cart-drawer__footer > .button-group (checkout button area)
    const buttonGroup = document.querySelector('.cart-drawer__footer .button-group');
    if (buttonGroup) {
      const savingsEl = document.createElement('div');
      savingsEl.className = 'bundle-cart-savings';
      savingsEl.innerHTML = `<span class="bundle-cart-savings__text">You save ${this.formatMoney(savingsCents)} (${discountPct}% off)</span>`;
      buttonGroup.parentNode.insertBefore(savingsEl, buttonGroup);
      return;
    }

    // Fallback: try other common selectors
    const fallback = document.querySelector('.cart__total, [data-cart-total], .mini-cart__total');
    if (fallback) {
      const savingsEl = document.createElement('div');
      savingsEl.className = 'bundle-cart-savings';
      savingsEl.innerHTML = `<span class="bundle-cart-savings__text">You save ${this.formatMoney(savingsCents)} (${discountPct}% off)</span>`;
      fallback.parentNode.insertBefore(savingsEl, fallback);
    }
  }

  /**
   * Apply a discount code via hidden iframe (proven Shopify pattern).
   * The iframe loads /discount/CODE which sets the discount on the session.
   * If the iframe fails (e.g. dev server 401), falls back to page redirect.
   */
  applyDiscountViaIframe(code) {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');

      let resolved = false;
      const done = () => {
        if (resolved) return;
        resolved = true;
        setTimeout(() => { try { iframe.remove(); } catch(e) {} }, 200);
        resolve();
      };

      // Success: iframe loaded the discount URL
      iframe.onload = () => { setTimeout(done, 400); };

      // Error: iframe failed (dev server 401, CORS, etc.) — fall back to redirect
      iframe.onerror = () => {
        try { iframe.remove(); } catch(e) {}
        // Redirect fallback — navigates the page but guarantees discount is applied
        window.location.href = `/discount/${code}?redirect=/cart`;
      };

      // Timeout safety: if iframe takes too long, resolve and open cart anyway
      setTimeout(done, 3000);

      iframe.src = `/discount/${code}?redirect=/`;
      document.body.appendChild(iframe);
    });
  }

  /* ---------------------------------------------------------------
   * Helpers
   * --------------------------------------------------------------- */

  /** Get the root wrapper element (footer is outside <bundle-builder>) */
  getRootEl() {
    return this.closest('.build-your-set-wrap') || this.closest('.section-stack') || document;
  }

  /**
   * Swap the displayed image on a charm card.
   * Clears srcset (which otherwise overrides src) and updates src.
   */
  swapCardImage(card, imageUrl) {
    if (!imageUrl) return;
    card.dataset.image = imageUrl;
    const img = card.querySelector('.product-card__image--primary') || card.querySelector('.bundle-charm-card__image');
    if (img) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = imageUrl;
    }
  }

  getSubtotal() {
    let total = 0;
    this.state.bases.forEach(b => { total += b.price * b.quantity; });
    this.state.charms.forEach(c => { total += c.price * (c.quantity || 1); });
    return total;
  }

  getTotalItemCount() {
    let count = 0;
    this.state.bases.forEach(b => { count += b.quantity; });
    this.state.charms.forEach(c => { count += (c.quantity || 1); });
    return count;
  }

  getCurrentTier() {
    const count = this.getTotalItemCount();
    let currentTier = null;
    for (const tier of this.tiers) {
      if (count >= tier.min) currentTier = tier;
    }
    return currentTier;
  }

  getNextTier() {
    const count = this.getTotalItemCount();
    for (const tier of this.tiers) {
      if (count < tier.min) return tier;
    }
    return null;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  centsToDecimal(cents) {
    return Number((Number(cents) / 100).toFixed(2));
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  pushDataLayer(eventName, ecommerceData) {
    const dl = window.dataLayer = window.dataLayer || [];
    dl.push({ ecommerce: null });
    dl.push({
      event: eventName,
      ecommerce: Object.assign(
        { currency: document.querySelector('meta[property="og:price:currency"]')?.content || 'USD' },
        ecommerceData
      )
    });
  }

  /** Push a non-ecommerce custom event (flat, not wrapped in ecommerce object) */
  pushCustomEvent(eventName, data) {
    const dl = window.dataLayer = window.dataLayer || [];
    dl.push({ event: eventName, ...data });
  }
}

// Register the custom element
if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
